import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import { Box, Grid, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	PAYROLL_LIST_SCREEN_SLUG,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDate, sortDDLByLabel, useNavigateToLogout, useRemoveQueriesOnUnmount } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { CalculatedPayrollDataGrid } from "./CalculatedPayrollDataGrid";
import { CalculatedPayrollSetupHeaderPanel } from "./Panels/CalculatedPayrollSetupHeaderPanel";
import { CalculatedPayrollSetupToolbar } from "./CalculatedPayrollSetupToolbar";
import CircularProgress from '@mui/material/CircularProgress';
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { EnhancedSnackbarForLinkMessage } from "../EnhancedSnackbarForLinkMessage";
import FileSaver from "file-saver";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import ImportPayrollDataPanel from "./Panels/ImportPayrollData";
import { PayrollDataGrid } from "./PayrollDataGrid";
import { PayrollService } from "../../services/PayrollService";
import { PayrollSetupHeaderPanel } from "./Panels/PayrollSetupHeaderPanel";
import { PayrollSetupToolbar } from "./PayrollSetupToolbar";
import { StickyHorizontalScrollbar } from "./StickyHorizontalScrollbar";
import { setPayrollFilters } from "../../store/actions/payrollFiltersActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IPayroll { }
const PAYROLL_LIST_QUERY_KEY = "payroll-list";
const PAYROLL_ROLL_BACK_LIST_QUERY_KEY = "payroll-rollback-list";
const CALCULATED_PAYROLL_LIST_QUERY_KEY = "calculated-payroll-list";
const CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY = "rollback-calculated-payroll-list";
// const PAY_FREQUENCY_DDL_LIST_QUERY_KEY = "pay-frequency-ddl";
const PAYROLL_PERIODS_DDL_LIST_QUERY_KEY = "payroll-periods-ddl";
// const DEPARTMENTS_DDL_LIST_QUERY_KEY = "department-ddl";
const COMBINE_API_FOR_CLIENT_DETAILS_AND_FREQUENCY_DDL = "COMBINE-API-FOR-CLIENT-DETAILS-AND-FREQUENCY-DDL";
const useGetCombineClientDetailsAndFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [COMBINE_API_FOR_CLIENT_DETAILS_AND_FREQUENCY_DDL, clientId, taxYear],
		queryFn: (data) => {
			return PayrollService.getCombineClientDetailsAndFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


// const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
// 	useQuery({
// 		queryKey: [PAY_FREQUENCY_DDL_LIST_QUERY_KEY, clientId, taxYear],
// 		queryFn: (data) => {
// 			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const useGetPayrollPeriodsDDL = (
	clientId: string, payPeriodId: string | null, taxYear: number
) =>
	useQuery({
		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, taxYear],
		queryFn: (data) => {
			return PayrollService.getPayrollPeriodsDDL(clientId, payPeriodId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

// const useGetVocationalEarningsList = (clientId: string) =>
// 	useQuery({
// 		queryKey: ["vocational-earnings-list"],
// 		queryFn: () => {
// 			return PayrollService.getVocationalEarningsList(clientId);
// 		},
// 		refetchOnWindowFocus: false,
// 	});
// const useGetDepartmentsDDL = (clientId: string) =>
// 	useQuery({
// 		queryKey: ["deaprtments-ddl", clientId],
// 		queryFn: () => {
// 			return DepartmentsService.getGetDepartmentsDDL(clientId);
// 		},
// 		refetchOnWindowFocus: false,
// 	});
const useGetPayrollTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,

	name: string,
	startsWith: string,
	department: string,
	paidBy: string,
	pageSize: number,
	page: number,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			PAYROLL_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			name,
			startsWith,
			department,
			paidBy,
			pageSize,
			page,
			params
		],
		queryFn: (data) => {
			return PayrollService.getPayroll(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				name,
				startsWith,
				department,
				paidBy,
				pageSize,
				page,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayrollRollBackTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,

	name: string,
	startsWith: string,
	department: string,
	paidBy: string,
	pageSize: number,
	page: number,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void,
	openRollbackDialogue: boolean
) =>
	useQuery({
		queryKey: [
			PAYROLL_ROLL_BACK_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			name,
			startsWith,
			department,
			paidBy,
			pageSize,
			page,
			params,
			openRollbackDialogue
		],
		queryFn: (data) => {
			return PayrollService.getRollBackPayroll(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				name,
				startsWith,
				department,
				paidBy,
				pageSize,
				page,
				params,
				navigateToLogout,
				openRollbackDialogue
			);
		},
		refetchOnWindowFocus: false,
		enabled: !!openRollbackDialogue,
	});


const useGetCalculatedPayrollTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,

	viewCalculated: string | number,
	nameCalculated: string,
	startsWithCalculated: string,
	departmentCalculated: string,
	pageSizeCalculated: number,
	pageCalculated: number,

	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			CALCULATED_PAYROLL_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			viewCalculated,
			nameCalculated,
			startsWithCalculated,
			departmentCalculated,
			pageSizeCalculated,
			pageCalculated,
			params
		],
		queryFn: (data) => {

			return PayrollService.getCalculatedPayroll(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				viewCalculated,
				nameCalculated,
				startsWithCalculated,
				departmentCalculated,
				pageSizeCalculated,
				pageCalculated,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});
const useGetCalculatedPayrollRollBackTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,

	viewCalculated: string | number,
	nameCalculated: string,
	startsWithCalculated: string,
	departmentCalculated: string,
	pageSizeCalculated: number,
	pageCalculated: number,

	params: IGetListRequest,
	navigateToLogout: (path: string) => void,
	openRollbackDialogue: boolean
) =>
	useQuery({
		queryKey: [
			CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			viewCalculated,
			nameCalculated,
			startsWithCalculated,
			departmentCalculated,
			pageSizeCalculated,
			pageCalculated,
			params,
			openRollbackDialogue
		],
		queryFn: (data) => {

			return PayrollService.getCalculatedPayrollRollBack(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				viewCalculated,
				nameCalculated,
				startsWithCalculated,
				departmentCalculated,
				pageSizeCalculated,
				pageCalculated,
				params,
				navigateToLogout,
				openRollbackDialogue
			);
		},
		refetchOnWindowFocus: false,
		enabled: !!openRollbackDialogue,
	});

// const EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY =
// 	"edr-earnings-single-data-record";
// const useGetEDREarningsAndTimeFormatData = (
// 	clientId: string,
// 	taxYear: number
// ) =>
// 	useQuery({
// 		queryKey: [EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY],
// 		queryFn: (data) => {
// 			return SalaryProfileService.getEDREarningsAndTimeFormatData(
// 				clientId,
// 				taxYear
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

// const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record-for-payroll";
// const useGetClientInformationByIdForPayroll = (
// 	id: string | undefined,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
// 		queryFn: (data) => {
// 			return ClientService.getSimpleClientByIdForPayroll(id, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

// const GENERAL_SETTING_INFO_DATA_QUERY_KEY = "general-settings-info-data-record";
// const useGetGeneralSettingsData = (
// 	clientId: string,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [GENERAL_SETTING_INFO_DATA_QUERY_KEY, clientId, navigateToLogout],
// 		queryFn: () => {
// 			return GeneralSettingsService.getByClientId(clientId, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});
export const PayrollSetup: React.FC<IPayroll> = () => {
	const gridsScrollContainerRef = useRef<HTMLDivElement | any>(null);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	// const isAdminUser = useSelector(
	// 	(state: IAppState) => state.user.profile.isSuperStatus
	// );
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	// const companyInfo = useSelector((state: IAppState) => state.dashboardApisData.client_detail);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const storedInvoiceNoId = useSelector((state: IAppState) => state.invoice.id);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();
	const screenPermissions = useSelector((state: IAppState) => state.drawerMenu.screenPermissions);
	const payrollScreenPermissions = screenPermissions.find((x: any) => x.url === '/payroll')


	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [loadingData, setIsLoading] = useState(false);
	const [payrollSkeletonLoading, setPayrollSkeletonLoading] = useState(false);
	const [processRollBackPayrollLoading, setProcessRollBackPayrollLoading] = useState(false);
	const [calculatedPayrollSkeletonLoading, setCalculatedPayrollSkeletonLoading] = useState(false);
	const [selectedPayPeriodId, setSelectedPayPeriodId] = useState<null | string>(null);
	const [selectedpayPeriodIndex, setSelectedPayPeriodIndex] = useState<null | number>(null);
	const [payPeriodStarDate, setPayPeriodStarDate] = useState<null | string>(null);
	const [payPeriodEndDate, setPayPeriodEndDate] = useState<null | string>(null);
	// const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [payrollSetupTableData, setPayrollTableData] =
		useState([]);
	// const [remittanceFrequencyId, setRemittanceFrequencyId] = useState(undefined);
	const [isPayrollProcessed, setIsPayrollProcessed] = useState(false);
	const [clearAllSelectedItemsForPayrollTable, setClearAllSelectedItemsForPayrollTable] = useState(0);
	const [clearAllSelectedItemsForCalculatedPayrollTable, setClearAllSelectedItemsForCalculatedPayrollTable] = useState(0);

	// payroll state filters
	const [nameFilter, setNameFilter] = useState("firstName");
	const [showWithAlphabetFilter, SetShowWithAlphabetFilter] = useState("all");
	const [paidByFilter, setPaidByFilter] = useState("all");
	const [departmentsFilter, setDepartmentsFilter] = useState("all");
	const [rowsPerPageItemsFilter, setRowsPerPageItemsFilter] = useState(100);
	const [pageFilter, setPageFilter] = useState(1);

	// calculated state filters
	const [nameCalculatedFilter, setNameCalculatedFilter] = useState("firstName");
	const [showWithAlphabetCalculatedFilter, setShowWithAlphabetCalculatedFilter] = useState("all");
	const [departmentsCalculatedFilter, setDepartmentsCalculatedFilter] = useState("all");
	const [viewCalculatedFilter, setViewCalculatedFilter] = useState("all");
	const [rowsPerPageItemsCalculatedFilter, setRowsPerPageItemsCalculatedFilter] = useState(100);
	const [pageCalculatedFilter, setPageCalculatedFilter] = useState(1);


	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});


	// let {
	// 	nameStored,
	// 	startsWithStored,
	// 	departmentStored,
	// 	paidByStored,
	// 	pageSizeStored,
	// 	pageStored,
	// 	viewCalculatedStored,
	// 	nameCalculatedStored,
	// 	startsWithCalculatedStored,
	// 	departmentCalculatedStored,
	// 	pageSizeCalculatedStored,
	// 	pageCalculatedStored,
	// } = useSelector((state: IAppState) => {
	// 	return {
	// 		nameStored: state.payrollFilters.name,
	// 		startsWithStored: state.payrollFilters.startsWith,
	// 		departmentStored: state.payrollFilters.department,
	// 		paidByStored: state.payrollFilters.paidBy,
	// 		pageSizeStored: state.payrollFilters.pageSize,
	// 		pageStored: state.payrollFilters.page,
	// 		viewCalculatedStored: state.payrollFilters.viewCalculated,
	// 		nameCalculatedStored: state.payrollFilters.nameCalculated,
	// 		startsWithCalculatedStored: state.payrollFilters.startsWithCalculated,
	// 		departmentCalculatedStored: state.payrollFilters.departmentCalculated,
	// 		pageSizeCalculatedStored: state.payrollFilters.pageSizeCalculated,
	// 		pageCalculatedStored: state.payrollFilters.pageCalculated,
	// 	};
	// });

	const nameStored = useSelector((state: IAppState) => state.payrollFilters.name);
	const startsWithStored = useSelector((state: IAppState) => state.payrollFilters.startsWith);
	const departmentStored = useSelector((state: IAppState) => state.payrollFilters.department);
	const paidByStored = useSelector((state: IAppState) => state.payrollFilters.paidBy);
	const pageSizeStored = useSelector((state: IAppState) => state.payrollFilters.pageSize);
	const pageStored = useSelector((state: IAppState) => state.payrollFilters.page);
	const viewCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.viewCalculated);
	const nameCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.nameCalculated);
	const startsWithCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.startsWithCalculated);
	const departmentCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.departmentCalculated);
	const pageSizeCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.pageSizeCalculated);
	const pageCalculatedStored = useSelector((state: IAppState) => state.payrollFilters.pageCalculated);

	// let {
	// 	accountantStored,
	// 	clientStored,
	// 	clientTypeStored,
	// 	typeStored,
	// 	transactionMethodStored,
	// 	dateAppliesOnStored,
	// 	startDateStored,
	// 	endDateStored,
	// } = useSelector((state: IAppState) => {
	// 	return {
	// 		accountantStored: state.transactionsHeaders.accountant,
	// 		clientStored: state.transactionsHeaders.client,
	// 		clientTypeStored: state.transactionsHeaders.clientType,
	// 		typeStored: state.transactionsHeaders.type,
	// 		transactionMethodStored: state.transactionsHeaders.transactionMethod,
	// 		dateAppliesOnStored: state.transactionsHeaders.dateAppliesOn,
	// 		startDateStored: state.transactionsHeaders.startDate,
	// 		endDateStored: state.transactionsHeaders.endDate,
	// 	};
	// });

	const name = formMethods.watch("name");
	const startsWith = formMethods.watch("startsWith");
	const department = formMethods.watch("department");
	const paidBy = formMethods.watch("paidBy");
	const pageSize = formMethods.watch("pageSize");
	const page = formMethods.watch("page");
	const viewCalculated = formMethods.watch("viewCalculated");
	const nameCalculated = formMethods.watch("nameCalculated");
	const startsWithCalculated = formMethods.watch("startsWithCalculated");
	const departmentCalculated = formMethods.watch("departmentCalculated");
	const pageSizeCalculated = formMethods.watch("pageSizeCalculated");
	const pageCalculated = formMethods.watch("pageCalculated");

	// const {
	// 	name,
	// 	startsWith,
	// 	department,
	// 	paidBy,
	// 	pageSize,
	// 	page,
	// 	viewCalculated,
	// 	nameCalculated,
	// 	startsWithCalculated,
	// 	departmentCalculated,
	// 	pageSizeCalculated,
	// 	pageCalculated,
	// } = formMethods.watch();


	useEffect(() => {
		if (nameStored && nameStored !== name) {
			formMethods.setValue("name", nameStored);
		}

		if (startsWithStored && startsWithStored !== startsWith) {
			formMethods.setValue("ShowWithAlphabet", startsWithStored);
		}

		if (departmentStored && departmentStored !== department) {
			formMethods.setValue("departments", departmentStored);
		}

		if (paidByStored && paidByStored !== paidBy) {
			formMethods.setValue("paidBy", paidByStored);
		}

		if (pageSizeStored && pageSizeStored !== pageSize) {
			formMethods.setValue("rowsPerPageItems", pageSizeStored);
		}

		if (pageStored && pageStored !== page) {
			formMethods.setValue("page", pageStored);
		}

		if (
			viewCalculatedStored !== undefined &&
			viewCalculatedStored !== viewCalculated
		) {
			formMethods.setValue("viewCalculated", viewCalculatedStored);
		}

		if (nameCalculatedStored && nameCalculatedStored !== nameCalculated) {
			formMethods.setValue("nameCalculated", nameCalculatedStored);
		}

		if (
			startsWithCalculatedStored &&
			startsWithCalculatedStored !== startsWithCalculated
		) {
			formMethods.setValue(
				"ShowWithAlphabetCalculated",
				startsWithCalculatedStored
			);
		}

		if (
			departmentCalculatedStored &&
			departmentCalculatedStored !== departmentCalculated
		) {
			formMethods.setValue(
				"departmentsCalculated",
				departmentCalculatedStored
			);
		}

		if (
			pageSizeCalculatedStored &&
			pageSizeCalculatedStored !== pageSizeCalculated
		) {
			formMethods.setValue(
				"rowsPerPageItemsCalculated",
				pageSizeCalculatedStored
			);
		}

		if (
			pageCalculatedStored &&
			pageCalculatedStored !== pageCalculated
		) {
			formMethods.setValue("pageCalculated", pageCalculatedStored);
		}
	}, [
		nameStored,
		startsWithStored,
		departmentStored,
		paidByStored,
		pageSizeStored,
		pageStored,
		viewCalculatedStored,
		nameCalculatedStored,
		startsWithCalculatedStored,
		departmentCalculatedStored,
		pageSizeCalculatedStored,
		pageCalculatedStored,
	]);

	const clearAllPayrollDataGridRowsSelections = useCallback(() => {
		setClearAllSelectedItemsForPayrollTable((prev) => prev + 1);
		setPayrollDataGridSelectedRows([]);
	}, []);

	const clearAllCalculatedPayrollDataGridRowsSelections = useCallback(() => {
		setClearAllSelectedItemsForCalculatedPayrollTable((prev) => prev + 1);
		setCalculatedPayrollDataGridSelectedRows([]);
	}, []);

	const [openImportPayrollDataPanel, setOpenImportPayrollDataPanel] = useState(false);
	const [openRollbackDialogue, setOpenRollbackDialogue] = useState(false);



	const [queryParamsPayrollData, setQueryParamsPayrollData] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 100,
	});
	const [queryParamsCalculatedPayrollData, setQueryParamsCalculatedPayrollData] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 100,
	});

	const invalidateAllPayrollQueries = useCallback(() => {
		return Promise.all([
			queryClient.invalidateQueries({ queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear] }),
			queryClient.invalidateQueries({ queryKey: [PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, nameFilter, showWithAlphabetFilter, departmentsFilter, paidByFilter, rowsPerPageItemsFilter, pageFilter, queryParamsPayrollData] }),
			queryClient.invalidateQueries({ queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, viewCalculatedFilter, nameCalculatedFilter, showWithAlphabetCalculatedFilter, departmentsCalculatedFilter, rowsPerPageItemsCalculatedFilter, pageCalculatedFilter, queryParamsCalculatedPayrollData] }),
			queryClient.invalidateQueries({ queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, nameFilter, showWithAlphabetFilter, departmentsFilter, paidByFilter, rowsPerPageItemsFilter, pageFilter, queryParamsPayrollData, openRollbackDialogue] }),
			queryClient.invalidateQueries({ queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, viewCalculatedFilter, nameCalculatedFilter, showWithAlphabetCalculatedFilter, departmentsCalculatedFilter, rowsPerPageItemsCalculatedFilter, pageCalculatedFilter, queryParamsCalculatedPayrollData, openRollbackDialogue] }),
		]);
	}, [clientId, selectedPayPeriodId, taxYear, payPeriodStarDate, payPeriodEndDate, nameFilter, showWithAlphabetFilter, departmentsFilter, paidByFilter, rowsPerPageItemsFilter, pageFilter, queryParamsPayrollData, viewCalculatedFilter, nameCalculatedFilter, showWithAlphabetCalculatedFilter, departmentsCalculatedFilter, rowsPerPageItemsCalculatedFilter, pageCalculatedFilter, queryParamsCalculatedPayrollData, openRollbackDialogue]);


	const { isPending: isPendingCombineClientDetailsAndFrequencyDDL, data: combineClientDetailsAndFrequencyDDL } =
		useGetCombineClientDetailsAndFrequencyDDL(clientId, taxYear);

	// const { isPending: isPendingClientInformationById, data: companyInfo } = useGetClientInformationByIdForPayroll(
	// 	clientId,
	// 	navigateToLogout
	// );
	// const { isPending: isPendingGeneralSettingsInformationById, data: general_settings } = useGetGeneralSettingsData(
	// 	clientId,
	// 	navigateToLogout
	// );
	// const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
	// 	useGetPayFrequencyDDL(clientId, taxYear);

	useEffect(() => {
		if (combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl.length === 1) {
			setSelectedPayPeriodId(combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl[0].value)
			let path = `/payroll/${combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl[0].value}`
			navigate(path)
		}
	}, [combineClientDetailsAndFrequencyDDL])

	const { id } = useParams();

	const { isPending, data } = useGetPayrollTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,

		nameFilter,
		showWithAlphabetFilter,
		departmentsFilter,
		paidByFilter,
		rowsPerPageItemsFilter,
		pageFilter,
		queryParamsPayrollData,
		navigateToLogout
	);
	const payrollTableData: any | null = data;

	const { isPending: isPendingPayrollRollBackTableDate, data: payrollRollBackTableData }: any = useGetPayrollRollBackTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,

		nameFilter,
		showWithAlphabetFilter,
		departmentsFilter,
		paidByFilter,
		rowsPerPageItemsFilter,
		pageFilter,
		queryParamsPayrollData,
		navigateToLogout,
		openRollbackDialogue
	);


	// // get list calls when setting update in any other tab 
	// useEffect(() => {
	// 	const handleStorageChange = (event: StorageEvent) => {
	// 		if (event.key === 'generalSettingsUpdated') {
	// 			queryClient.invalidateQueries({
	// 				queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
	// 					selectedPayPeriodId,
	// 					payPeriodStarDate,
	// 					payPeriodEndDate,
	// 					queryParamsPayrollData,
	// 				],
	// 			});
	// 		}
	// 	};

	// 	window.addEventListener('storage', event => handleStorageChange(event));
	// 	return () => window.removeEventListener('storage', handleStorageChange);
	// }, [clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, queryParamsPayrollData]);

	useEffect(() => {
		const WATCHED_KEYS: Record<string, unknown[]> = {
			generalSettingsUpdated: [PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate,
				nameFilter,
				showWithAlphabetFilter,
				departmentsFilter,
				paidByFilter,
				rowsPerPageItemsFilter,
				pageFilter,
				queryParamsPayrollData],
			earnings: [PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate,
				nameFilter,
				showWithAlphabetFilter,
				departmentsFilter,
				paidByFilter,
				rowsPerPageItemsFilter,
				pageFilter,
				queryParamsPayrollData],
			deductions: [PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate,
				nameFilter,
				showWithAlphabetFilter,
				departmentsFilter,
				paidByFilter,
				rowsPerPageItemsFilter,
				pageFilter,
				queryParamsPayrollData],
			reimbursements: [PAYROLL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate,
				nameFilter,
				showWithAlphabetFilter,
				departmentsFilter,
				paidByFilter,
				rowsPerPageItemsFilter,
				pageFilter,
				queryParamsPayrollData],
		};

		const handleStorageChange = (event: StorageEvent) => {
			if (!event.key || !WATCHED_KEYS[event.key]) return;

			queryClient.invalidateQueries({
				queryKey: WATCHED_KEYS[event.key],
			});
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);

	}, [clientId, selectedPayPeriodId, payPeriodStarDate, payPeriodEndDate, queryParamsPayrollData]);

	// const {
	// 	isPending: isPendingGetEmployeeDDLByStatus,
	// 	data: edrEarningsAndTimeFormatData,
	// } = useGetEDREarningsAndTimeFormatData(clientId, taxYear);


	const { isPending: isPendingCalculatedPayrollTableData, data: calculatedPayrollTableData } = useGetCalculatedPayrollTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,
		viewCalculatedFilter,
		nameCalculatedFilter,
		showWithAlphabetCalculatedFilter,
		departmentsCalculatedFilter,
		rowsPerPageItemsCalculatedFilter,
		pageCalculatedFilter,
		queryParamsCalculatedPayrollData,
		navigateToLogout
	);
	const { isPending: isPendingCalculatedPayrollRollBackTableData, data: calculatedPayrollRollBackTableData } = useGetCalculatedPayrollRollBackTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,
		viewCalculatedFilter,
		nameCalculatedFilter,
		showWithAlphabetCalculatedFilter,
		departmentsCalculatedFilter,
		rowsPerPageItemsCalculatedFilter,
		pageCalculatedFilter,
		queryParamsCalculatedPayrollData,
		navigateToLogout,
		openRollbackDialogue
	);

	const {
		isPending: isPendingPayrollPeriodsDDL,
		data: payrollPeriodsDDL,
	} = useGetPayrollPeriodsDDL(clientId, selectedPayPeriodId, taxYear);

	// const {
	// 	isPending: isPendingVocationalEarningsList,
	// 	data: vocationalEarningsList,
	// } = useGetVocationalEarningsList(clientId);

	const updatedVocationalEarningsList = useMemo(() => {
		if (combineClientDetailsAndFrequencyDDL && !combineClientDetailsAndFrequencyDDL.payroll_vocational_earning?.vocationableEarnings) return combineClientDetailsAndFrequencyDDL.payroll_vocational_earning;

		if (combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_vocational_earning) {
			const updatedEarnings = Object.fromEntries(
				Object.entries(combineClientDetailsAndFrequencyDDL.payroll_vocational_earning?.vocationableEarnings).map(([provinceId, fields]: any) => {
					if (fields.includes("grossIncome")) {
						return [provinceId, fields];
					}

					const idx = fields.indexOf("regularGross");
					if (idx !== -1) {
						const updatedFields = [...fields];
						updatedFields[idx] = "grossIncome";
						return [provinceId, updatedFields];
					}

					return [provinceId, fields];
				})
			);

			return {
				...combineClientDetailsAndFrequencyDDL.payroll_vocational_earning,
				vocationableEarnings: updatedEarnings,
			};
		}
	}, [combineClientDetailsAndFrequencyDDL]);


	useEffect(() => {
		if (payrollPeriodsDDL && payrollPeriodsDDL.length > 0 && !isPayrollProcessed) {
			const isRollBack = payrollPeriodsDDL?.find((item: any) => item.rollBack === true) ?? false;
			if (isRollBack) {
				const clientDetails = combineClientDetailsAndFrequencyDDL?.client_details_for_payroll;
				if (clientDetails) {
					const province = clientDetails.address?.province?.shortCode;
					const isOntarioClient = province === "ON";
					const isQuebecApplicable = province === "QC" || Boolean(clientDetails.qcEmployees);

					const hasCraSetupComplete = Boolean(clientDetails.haveCra);

					const hasEhtSetupComplete = !isOntarioClient || Boolean(clientDetails.haveEht);
					const hasEhtSettingSetupComplete = !isOntarioClient || Boolean(clientDetails.haveEhtSetting);

					const hasQuebecComplianceComplete =
						!isQuebecApplicable ||
						(clientDetails.haveRq && clientDetails.haveCsst && clientDetails.haveHsf);

					const isFullyCompliant =
						hasCraSetupComplete && hasEhtSetupComplete && hasEhtSettingSetupComplete && hasQuebecComplianceComplete;

					if (isFullyCompliant) {
						formMethods.setValue('payrollPeriodsDDL', isRollBack.endDate)
						formMethods.setValue('payFrequencyDDLItems', isRollBack.payScheduleId)
						setPayPeriodStarDate(isRollBack.startDate)
						setPayPeriodEndDate(isRollBack.endDate)
						setOpenRollbackDialogue(true);
					} else {
						setOpenRollbackDialogue(false);
					}
				}
			} else {
				let lastItem = payrollPeriodsDDL.at(-1)
				const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === lastItem.value);
				formMethods.setValue('payrollPeriodsDDL', lastItem.endDate)
				formMethods.setValue('payFrequencyDDLItems', lastItem.payScheduleId)
				setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
				setPayPeriodStarDate(lastItem.startDate)
				setPayPeriodEndDate(lastItem.endDate)
			}

		}
		// }, [payrollPeriodsDDL, isPayrollProcessed])
	}, [payrollPeriodsDDL, isPayrollProcessed, combineClientDetailsAndFrequencyDDL])

	// const { data: departmentsDDL, isPending: isPendingDepartmentDDL } =
	// 	useGetDepartmentsDDL(clientId);


	const departmentsDDLItems = useMemo(() => {
		if (combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.department_ddl && Array.isArray(combineClientDetailsAndFrequencyDDL.department_ddl)) {
			let resources: any[] = sortDDLByLabel(combineClientDetailsAndFrequencyDDL.department_ddl).map((resourceItem: any) => ({
				id: resourceItem.value,
				label: resourceItem.label,
				value: resourceItem.value,
			}));
			resources.unshift({
				label: "Department: ALL",
				value: "all",
			});
			return resources;
		} else if (combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.department_ddl) {
			return [
				{
					label: "Department: ALL",
					value: "all",
				},
			];
		}
	}, [combineClientDetailsAndFrequencyDDL]);



	const { isPending: isSubmitting, mutate: submitPayroll } = useMutation({
		mutationFn: PayrollService.create,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				clearAllPayrollDataGridRowsSelections();
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isCalculatingPayroll, mutate: calculatePayroll } = useMutation({
		mutationFn: PayrollService.calculate,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				clearAllPayrollDataGridRowsSelections();
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isInsertingEmployeePayrollData, mutate: insertEmployeePayrollData } = useMutation({
		mutationFn: PayrollService.insertEmployeePayrollData,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				// const updatePayloadData =
				// 	payrollDataGridSelectedRows &&
				// 	payrollDataGridSelectedRows.map((item: any) => ({
				// 		...item,
				// 		clientId: clientId,
				// 		payPeriodStartDate: payPeriodStarDate,
				// 		payPeriodEndDate: payPeriodEndDate,
				// 		payrollSheduleId: selectedPayPeriodId,
				// 		vacationPayAccured: item.vacationPayAccured ? item.vacationPayAccured : item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined ?
				// 			item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined : undefined,
				// 		vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined: undefined,
				// 		// customEarnings: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.earnings),
				// 		// customDeductions: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.deductions),
				// 		// customReimbursements: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.reimbursements),
				// 		customEarnings: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.earnings),
				// 		customDeductions: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.deductions),
				// 		customReimbursements: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.reimbursements),
				// 	}));
				// (async () => {
				// 	try {
				// 		const saveData = PayrollService.create({ payrollData: updatePayloadData })
				// 	} catch { }
				// })();
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				clearAllPayrollDataGridRowsSelections();
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isEditingCalculatdePayroll, mutate: editingCalculatdePayroll } = useMutation({
		mutationFn: PayrollService.editingCalculatdePayroll,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				clearAllCalculatedPayrollDataGridRowsSelections();
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isSavingCalculatdePayroll, mutate: savingCalculatdePayroll } = useMutation({
		mutationFn: PayrollService.savingCalculatdePayroll,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});
	const { isPending: isOverwriteCalculatdePayroll, mutate: overwriteCalculatdePayroll } = useMutation({
		mutationFn: PayrollService.overwriteCalculatdePayroll,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				clearAllCalculatedPayrollDataGridRowsSelections();
				return invalidateAllPayrollQueries();
				// return Promise.all([
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData,],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			nameFilter,
				// 			showWithAlphabetFilter,
				// 			departmentsFilter,
				// 			paidByFilter,
				// 			rowsPerPageItemsFilter,
				// 			queryParamsPayrollData, openRollbackDialogue],
				// 	}),
				// 	queryClient.invalidateQueries({
				// 		queryKey: [CALCULATED_PAYROLL_ROLL_BACK_LIST_QUERY_KEY, clientId,
				// 			selectedPayPeriodId,
				// 			payPeriodStarDate,
				// 			payPeriodEndDate,
				// 			viewCalculatedFilter,
				// 			nameCalculatedFilter,
				// 			showWithAlphabetCalculatedFilter,
				// 			departmentsCalculatedFilter,
				// 			rowsPerPageItemsCalculatedFilter,
				// 			queryParamsCalculatedPayrollData, openRollbackDialogue],
				// 	}),
				// ]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const [openEmployeePayrollDataDialog, setDialogEmployeePayrollDataOpen] = useState(false);
	// const [openEmployeePayrollCalculatedDataDialog, setDialogEmployeePayrollCalculatedDataOpen] = useState(false);
	const [payrollDataGridSelectedRows, setPayrollDataGridSelectedRows] = useState([]);
	const [calculatedPayrollDataGridSelectedRows, setCalculatedPayrollDataGridSelectedRows] = useState([]);
	// const [selectedIds, setSelectedIds] = useState([]);
	// const [payrollSetupUpdatedItems, setPayrollUpdatedItems] =
	// 	useState<any>(undefined);

	// for employee payroll data delete row
	const [singleEmployeePayrollDataDelete, setEmployeePayrollDataDelete] = useState<boolean>(false);
	// const [payrollRowData, setPayrollRowData] = useState<any>(0);

	// const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
	// 	useState(undefined);
	// const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChangePayrollData = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParamsPayrollData(dispatchParams);
	}, []);
	const onRequestParamsChangeCalculatedPayrollData = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParamsCalculatedPayrollData(dispatchParams);
	}, []);

	const { isPending: isDeletingEmployeePayrollData, mutate: deleteEmployeePayrollDataById } =
		useMutation({
			mutationFn: PayrollService.delEmployeePayrollData,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the Payroll fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// setClearAllSelectedItemsForPayrollTable('delete')
					clearAllPayrollDataGridRowsSelections()
				} else {
					clearAllPayrollDataGridRowsSelections()
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong, try again later!",
						severity: "error",
					});
				}
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteEmployeePayrollDataField = () => {
		if (singleEmployeePayrollDataDelete) {
			setEmployeePayrollDataDelete(false);
			// if (payrollTableData && payrollTableData.rowSelection) {
			// if (payrollDataGridSelectedRows.length > 0) {
			const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
			let delEmployeePayrollDataPayload = {
				data: {
					clientId: clientId,
					payPeriodStartDate: payPeriodStarDate,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					employeeIds: empIds
				},
				navigateToLogout: navigateToLogout,
			};
			deleteEmployeePayrollDataById(delEmployeePayrollDataPayload);
			setDialogEmployeePayrollDataOpen(false);
			// } 
			// else {
			// 	setSnackbarData({
			// 		open: true,
			// 		message: "Please select at least one employee!",
			// 		severity: "error"
			// 	})
			// }
			// } 
			// else {
			// const empIds = payrollSetupTableData.map((x: any) => x.employeeId)

			// let delEmployeePayrollDataPayload = {
			// 	data: {
			// 		clientId: clientId,
			// 		payPeriodEndDate: payPeriodEndDate,
			// 		payrollSheduleId: selectedPayPeriodId,
			// 		employeeIds: empIds
			// 	},
			// 	navigateToLogout: navigateToLogout,
			// };
			// deleteEmployeePayrollDataById(delEmployeePayrollDataPayload);
			// setDialogEmployeePayrollDataOpen(false);
			// }
		}
	};

	// const onRowDeleteClicked = useCallback((params: any) => {
	// 	setDialogEmployeePayrollDataOpen(true);
	// 	setEmployeePayrollDataDelete(true);
	// 	setPayrollRowData(params);
	// }, []);

	// const onDeleteEmployeePayrollDataClicked = () => {
	// 	setDialogEmployeePayrollDataOpen(true);
	// 	setEmployeePayrollDataDelete(true);
	// 	// if (selectedDataRecord) {
	// 	// 	setAccountantId(selectedDataRecord.id);
	// 	// }
	// };
	const onDeleteEmployeePayrollDataClicked = () => {
		if (payrollTableData && payrollTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				setDialogEmployeePayrollDataOpen(true);
				setEmployeePayrollDataDelete(true);
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				});
			}
		} else {
			setDialogEmployeePayrollDataOpen(true);
			setEmployeePayrollDataDelete(true);
		}
	};

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/payroll/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseEmployeePayrollDataDeleteHandler = () => {
		setDialogEmployeePayrollDataOpen(false);
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		// let selectedItemsIds = selectedItems && selectedItems.map((item: any) => item.id);
		setPayrollDataGridSelectedRows(selectedItems);
		// return (<></>);
		// setPayrollDataGridSelectedRows(prev => {
		// 	const prevIds = prev.map((r: any) => r.id).join(',');
		// 	const nextIds = (selectedItems ?? []).map((r: any) => r.id).join(',');
		// 	return prevIds === nextIds ? prev : selectedItems;
		// });
		return null;
	}, []);
	const CalculatedPayrollDataGridSelectionToolbar = useCallback((selectedItems: any) => {
		// let selectedItemsIds = selectedItems && selectedItems.map((item: any) => item.id);
		setCalculatedPayrollDataGridSelectedRows(selectedItems);
		// return (
		// 	<></>
		// );
		// setCalculatedPayrollDataGridSelectedRows(prev => {
		// 	const prevIds = prev.map((r: any) => r.id).join(',');
		// 	const nextIds = (selectedItems ?? []).map((r: any) => r.id).join(',');
		// 	return prevIds === nextIds ? prev : selectedItems;
		// });
		return null;
	}, []);

	// const calculateVacationDue = (item: any, updatedVocationalEarningsList: any) => {
	// 	const updatedItem = { ...item };
	// 	const hoursField = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"].find(
	// 		field => item[field] != null && Number(item[field]) > 0
	// 	);
	// 	const allVocationableEarningsListOfThisEmployeeProvince = [
	// 		...(updatedVocationalEarningsList.vocationableEarnings[item.provinceId] ?? []),
	// 		...updatedVocationalEarningsList.edr.map((label: string) => `customAmounts.${label}`)
	// 	];
	// 	const getByPath = (obj: any, path: string) =>
	// 		path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

	// 	if (allVocationableEarningsListOfThisEmployeeProvince && allVocationableEarningsListOfThisEmployeeProvince.length > 0) {
	// 		if (
	// 			item.grossIncome && item.grossIncome !== null && item.grossIncome !== 0
	// 		) {
	// 			if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 				const isVocationable = allVocationableEarningsListOfThisEmployeeProvince.includes("grossIncome");
	// 				if (isVocationable) {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === "grossIncome"
	// 								? Number(item.grossIncome) || 0
	// 								: Number(getByPath(item, vocField)) || 0;
	// 						totalSum += fieldValue;
	// 					});
	// 					const calculatedValue = (totalSum * item.minVocationPayOfGross) / 100;
	// 					const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 					const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 					const savedVacationPay = Number(item.vacationPay) || 0;
	// 					const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 					if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 						let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 						updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 					}
	// 					if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 						updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 					}
	// 				}
	// 			} else {
	// 				let totalSum = 0;
	// 				const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 				allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 					if (notAllowedFields.includes(vocField)) return;
	// 					const fieldValue =
	// 						vocField === "grossIncome"
	// 							? Number(item.grossIncome) || 0
	// 							: Number(getByPath(item, vocField)) || 0;
	// 					totalSum += fieldValue;
	// 				});
	// 				const calculatedValue = (totalSum * item.minVocationPayOfGross) / 100;
	// 				const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 				const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 				if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 					updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 					updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 					updatedItem.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 				}
	// 				if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 					updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 				}
	// 			}
	// 		} else if (
	// 			!item.grossIncome || item.grossIncome === null || item.grossIncome === 0
	// 		) {
	// 			if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 				const allowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 				// const isVocationable = allowedFields.some(field =>
	// 				// 	allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 				// );
	// 				const matchedFields = allowedFields.filter(field =>
	// 					allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 				);
	// 				const matchedCount = matchedFields.length;
	// 				const hasRegular = matchedFields.includes("regularHours");
	// 				const hasOt1 = matchedFields.includes("ot1Dot5Hours");
	// 				const hasOt2 = matchedFields.includes("ot2Dot0Hours");
	// 				const isVocationable = matchedCount > 0;
	// 				if (isVocationable) {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					const allowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === "grossIncome"
	// 								? Number(item.grossIncome) || 0
	// 								: Number(getByPath(item, vocField)) || 0;

	// 						totalSum += fieldValue;
	// 					});
	// 					const regularHoursValue = hasRegular &&
	// 						Number(item.regularHours || 0) *
	// 						Number(item.hourlyRate || 0);
	// 					const ot1Dot5HoursValue = hasOt1 &&
	// 						Number(item.ot1Dot5Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 1.5;
	// 					const ot2Dot0HoursValue = hasOt2 &&
	// 						Number(item.ot2Dot0Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 2.0;
	// 					let grossValue = (regularHoursValue || 0) + (ot1Dot5HoursValue || 0) + (ot2Dot0HoursValue || 0) + totalSum
	// 					const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 					const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 					const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 					const savedVacationPay = Number(item.vacationPay) || 0;
	// 					const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 					if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 						let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 						updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 					}
	// 					if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 						updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 					}
	// 				}
	// 			} else {
	// 				let totalSum = 0;
	// 				const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 				const allowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 				allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 					if (notAllowedFields.includes(vocField)) return;
	// 					const fieldValue =
	// 						vocField === "grossIncome"
	// 							? Number(item.grossIncome) || 0
	// 							: Number(getByPath(item, vocField)) || 0;

	// 					totalSum += fieldValue;
	// 				});
	// 				let grossValue = totalSum
	// 				const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 				const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 				const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 				if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 					updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 					updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 					updatedItem.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 				}
	// 				if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 					updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 				}
	// 			}
	// 		} else if (hoursField) {
	// 			const allowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 			// const isVocationable = allowedFields.some(field =>
	// 			// 	allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 			// );
	// 			const matchedFields = allowedFields.filter(field =>
	// 				allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 			);
	// 			const matchedCount = matchedFields.length;
	// 			const hasRegular = matchedFields.includes("regularHours");
	// 			const hasOt1 = matchedFields.includes("ot1Dot5Hours");
	// 			const hasOt2 = matchedFields.includes("ot2Dot0Hours");
	// 			// const isVocationable = allVocationableEarningsListOfThisEmployeeProvince.includes(column.field);
	// 			const isVocationable = matchedCount > 0;
	// 			if (
	// 				isVocationable &&
	// 				(
	// 					!item.grossIncome ||
	// 					item.grossIncome === null ||
	// 					item.grossIncome === "" ||
	// 					item.grossIncome === "0.00" ||
	// 					Number(item.grossIncome) === 0
	// 				)
	// 			) {
	// 				if (hoursField === "regularHours") {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === hoursField
	// 								? Number(item.hoursField) || 0
	// 								: Number(getByPath(item, vocField)) || 0;

	// 						totalSum += fieldValue;
	// 					});
	// 					const ot1Dot5HoursValue =
	// 						Number(item.ot1Dot5Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 1.5;
	// 					const ot2Dot0HoursValue =
	// 						Number(item.ot2Dot0Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 2.0;
	// 					let grossValue = (item.regularHours * item.hourlyRate) + ot1Dot5HoursValue + ot2Dot0HoursValue + totalSum
	// 					const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 					if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 						const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 						const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 						const savedVacationPay = Number(item.vacationPay) || 0;
	// 						const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 						}
	// 					} else {
	// 						const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 						const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 							updatedItem["vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined"] = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 						}
	// 					}
	// 				} else if (hoursField === "ot1Dot5Hours") {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === hoursField
	// 								? Number(item.hoursField) || 0
	// 								: Number(getByPath(item, vocField)) || 0;

	// 						totalSum += fieldValue;
	// 					});
	// 					const regularHoursValue =
	// 						Number(item.regularHours || 0) *
	// 						Number(item.hourlyRate || 0);
	// 					const ot2Dot0HoursValue =
	// 						Number(item.ot2Dot0Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 2.0;
	// 					let grossValue = (item.ot1Dot5Hours * item.hourlyRate * 1.5) + regularHoursValue + ot2Dot0HoursValue + totalSum
	// 					const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 					if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 						const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 						const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 						const savedVacationPay = Number(item.vacationPay) || 0;
	// 						const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 						}
	// 					} else {
	// 						const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 						const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 							updatedItem["vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined"] = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 						}
	// 					}
	// 				} else if (hoursField === "ot2Dot0Hours") {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === hoursField
	// 								? Number(item.hoursField) || 0
	// 								: Number(getByPath(item, vocField)) || 0;

	// 						totalSum += fieldValue;
	// 					});
	// 					const regularHoursValue =
	// 						Number(item.regularHours || 0) *
	// 						Number(item.hourlyRate || 0);
	// 					const ot1Dot5HoursValue =
	// 						Number(item.ot1Dot5Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 1.5;
	// 					let grossValue = (item.ot2Dot0Hours * item.hourlyRate * 2.0) + regularHoursValue + ot1Dot5HoursValue + totalSum
	// 					const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 					if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 						const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 						const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 						const savedVacationPay = Number(item.vacationPay) || 0;
	// 						const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 							updatedItem["vacationPayAccured"] = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 						}
	// 					} else {
	// 						const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 						const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 							updatedItem["vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined"] = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 						}
	// 					}
	// 				}
	// 			}
	// 		} else {
	// 			if (
	// 				item.grossIncome !== null && item.grossIncome !== 0
	// 			) {

	// 				const isVocationable = allVocationableEarningsListOfThisEmployeeProvince.includes("grossIncome");
	// 				if (isVocationable) {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === "grossIncome"
	// 								? Number(item.grossIncome) || 0
	// 								: Number(getByPath(item, vocField)) || 0;
	// 						totalSum += fieldValue;
	// 					});

	// 					const calculatedValue = (totalSum * item.minVocationPayOfGross) / 100;
	// 					if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 						const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 						const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 						const savedVacationPay = Number(item.vacationPay) || 0;
	// 						const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 						}
	// 					} else {
	// 						const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 						const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 						}
	// 					}
	// 				}
	// 			} else if (
	// 				item.grossIncome === null || item.grossIncome === 0
	// 			) {
	// 				const allowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 				const isVocationable = allowedFields.some(field =>
	// 					allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 				);
	// 				const matchedFields = allowedFields.filter(field =>
	// 					allVocationableEarningsListOfThisEmployeeProvince.includes(field)
	// 				);
	// 				const matchedCount = matchedFields.length;
	// 				const hasRegular = matchedFields.includes("regularHours");
	// 				const hasOt1 = matchedFields.includes("ot1Dot5Hours");
	// 				const hasOt2 = matchedFields.includes("ot2Dot0Hours");
	// 				if (isVocationable) {
	// 					let totalSum = 0;
	// 					const notAllowedFields = ["regularHours", "ot1Dot5Hours", "ot2Dot0Hours"];
	// 					allVocationableEarningsListOfThisEmployeeProvince.forEach((vocField: string) => {
	// 						if (notAllowedFields.includes(vocField)) return;
	// 						const fieldValue =
	// 							vocField === "grossIncome"
	// 								? Number(item.grossIncome) || 0
	// 								: Number(getByPath(item, vocField)) || 0;

	// 						totalSum += fieldValue;
	// 					});
	// 					const regularHoursValue = hasRegular &&
	// 						Number(item.regularHours || 0) *
	// 						Number(item.hourlyRate || 0);
	// 					const ot1Dot5HoursValue = hasOt1 &&
	// 						Number(item.ot1Dot5Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 1.5;
	// 					const ot2Dot0HoursValue = hasOt2 &&
	// 						Number(item.ot2Dot0Hours || 0) *
	// 						Number(item.hourlyRate || 0) * 2.0;
	// 					let grossValue = (regularHoursValue || 0) + (ot1Dot5HoursValue || 0) + (ot2Dot0HoursValue || 0) + totalSum
	// 					const calculatedValue = (grossValue * item.minVocationPayOfGross) / 100;
	// 					if (item.hasOwnProperty("vacationPayAccured") && item["vacationPayAccured"] != null) {
	// 						const previousSavedVacDue = Number(item.vacationPayAccured) || 0;
	// 						const previousPeriodVacDue = Number(item.vacationPayAccuredTotal) || 0;
	// 						const savedVacationPay = Number(item.vacationPay) || 0;
	// 						const newValue = savedVacationPay + previousPeriodVacDue + calculatedValue - previousSavedVacDue;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							let latestValue = newValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(Math.round(latestValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(Math.round(newValue * 100) / 100);
	// 						}
	// 					} else {
	// 						const previousPeriodVacDue = item.vacationPayAccuredTotal ? Number(item.vacationPayAccuredTotal) : 0
	// 						const previousSavedVacDue = item.vacationPayAccured ? Number(item.vacationPayAccured) : 0;
	// 						const newValue = previousPeriodVacDue + calculatedValue + Number(updatedItem.vacationPayOpenBalance) || 0;
	// 						if (updatedItem.vacationPayFlag && updatedItem.vacationPayFlag === 100) {
	// 							updatedItem.vacationPayAccured = formatAmountCommaSeperator(newValue);
	// 							updatedItem.vacationPayAccuredCalculated = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 							updatedItem.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined = formatAmountCommaSeperator(Math.round(calculatedValue * 100) / 100);
	// 						}
	// 						if (updatedItem.payOutVacationPayFlag && updatedItem.payOutVacationPayFlag === 200) {
	// 							updatedItem.vacationPay = formatAmountCommaSeperator(newValue);
	// 						}
	// 					}

	// 				}
	// 			}
	// 		}
	// 		return updatedItem;
	// 	} else {
	// 		return item
	// 	}
	// }
	// const upadtedPayrollItems = useMemo(() => {
	// 	if (payrollTableData && payrollTableData.items) {
	// 	return payrollTableData.items;
	// 	}
	// }, [payrollTableData]);

	// const upadtedPayrollItems = useMemo(() => {
	// 	if (!payrollTableData?.items) {
	// 		return [];
	// 	}

	// 	return payrollTableData.items.map((item: any) =>
	// 		calculateVacationDue(item, updatedVocationalEarningsList)
	// 	);
	// }, [payrollTableData, updatedVocationalEarningsList]);

	// const upadtedCalculatedPayrollItems = useMemo(() => {
	// 	if (calculatedPayrollTableData && calculatedPayrollTableData.items) {
	// 		return calculatedPayrollTableData.items;
	// 	}
	// }, [calculatedPayrollTableData]);


	// const upadtedPayrollRollBackItems = useMemo(() => {
	// 	if (!payrollRollBackTableData?.items) {
	// 		return [];
	// 	}

	// 	return payrollRollBackTableData && payrollRollBackTableData.items.map((item: any) =>
	// 		calculateVacationDue(item, updatedVocationalEarningsList)
	// 	);
	// }, [payrollRollBackTableData, updatedVocationalEarningsList]);

	// const upadtedCalculatedPayrollRollBackItems = useMemo(() => {
	// 	if (calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.items) {
	// 		return calculatedPayrollRollBackTableData.items;
	// 	}
	// }, [calculatedPayrollRollBackTableData]);



	// const expandedLabels = ["Payroll"];
	// const expandedPayrollOrSetupItem = "Payroll"; // Special case for "List" under "Payroll"
	// const enhanceData = (data: any[], parentLabel?: string): any[] => {
	// 	return data.map((item) => {
	// 		let isExpanded = expandedLabels.includes(item.label);
	// 		if (
	// 			parentLabel === "Payroll" &&
	// 			item.label === expandedPayrollOrSetupItem
	// 		) {
	// 			isExpanded = true;
	// 		}
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: isExpanded,
	// 			items: item.items ? enhanceData(item.items, item.label) : undefined,
	// 		};
	// 		return enhancedItem;
	// 	});
	// };
	const expandedLabels = ["Payroll"];
	const expandedPayrollOrSetupItem = "Payroll";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payroll" &&
				item.label === expandedPayrollOrSetupItem
			) {
				isExpanded = true;
			}

			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedPayrollOrSetupItem && "/payroll",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: PAYROLL_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const onSubmit = (values: any) => {

	};

	useEffect(() => {
		if (id && !selectedPayPeriodId) {
			setSelectedPayPeriodId(id)
			formMethods.setValue('payFrequencyDDLItems', id)
		}
	}, [id])
	const onFrequencyChangeHandler = async (selectedItem: any) => {
		setIsPayrollProcessed(false)
		setSelectedPayPeriodId(selectedItem.value)
		let path = `/payroll/${selectedItem.value}`
		navigate(path)
	};
	const onPayrollPeriodsDDLChangeHandler = async (selectedItem: any) => {
		const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

		if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
			const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
			const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

			setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
			setPayPeriodStarDate(selectedPayrollPeriod.startDate)
			setPayPeriodEndDate(selectedPayrollPeriod.endDate)
		}
	};

	const buildCustomEarningsArray = (
		item: any,
		edrList: { label: string; value: string }[]
	) => {
		return edrList.map(edr => {
			const key = `customAmounts.${edr.label}`;

			return {
				id: edr.value,
				label: edr.label,
				value: Object.prototype.hasOwnProperty.call(item, key)
					? item[key]
					: item.customAmounts[edr.label]
			};
		});
	};

	const onSubmitButtonClickedHandler = () => {

		if (payrollTableData && payrollTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				const updatePayloadData =
					payrollDataGridSelectedRows &&
					payrollDataGridSelectedRows.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriodStartDate: payPeriodStarDate,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						// vacationPayAccured: item.vacationPayAccured ? parseFloat(item.vacationPayAccured) : item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined ?
						// 	parseFloat(item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined) : undefined,
						// vacationPayAccured: item.vacationPayAccuredCalculated ? parseFloat(item.vacationPayAccuredCalculated) : item.vacationPayAccured ?
						// 	parseFloat(item.vacationPayAccured) : undefined,
						vacationPayAccured: item.vacationPayAccuredCalculated ? parseFloat(item.vacationPayAccuredCalculated) : null,
						vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined: undefined,
						customEarnings: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.earnings),
						customDeductions: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.deductions),
						customReimbursements: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.reimbursements),
						// vacationPayAccured: parseFloat(item.vacationPayAccured)
					}));



				submitPayroll({ payrollData: updatePayloadData })


				// this will use in calculate button

				// updatePayloadData.forEach((item, index) => {
				// 	const hasGross = item.grossIncomeIncome != null && item.grossIncome !== "";

				// 	// check if any of the hour fields have a non-null/non-empty value
				// 	const hasAnyHours =
				// 		(item.regularHours != null && item.regularHours !== "") ||
				// 		(item.ot1Dot5Hours != null && item.ot1Dot5Hours !== "") ||
				// 		(item.ot2Dot0Hours != null && item.ot2Dot0Hours !== "");

				// 	if (hasGross && !hasAnyHours) {
				// 		setSnackbarData({
				// 			open: true,
				// 			message: "hours are required with Gross!",
				// 			severity: "error"
				// 		})
				// 	} else {
				// 
				// 		// submitPayroll({ payrollData: updatePayloadData })
				// 	}
				// });
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const updatePayloadData =
				payrollSetupTableData &&
				payrollSetupTableData.map((item: any) => ({
					...item,
					clientId: clientId,
					payPeriodStartDate: payPeriodStarDate,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					customEarnings: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.earnings),
					customDeductions: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.deductions),
					customReimbursements: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.reimbursements),
					vacationPayAccured: parseFloat(item.vacationPayAccured)
				}));

			submitPayroll({ payrollData: updatePayloadData })
		}
	};
	const onCalculatePayrollClicked = async () => {
		if (payrollTableData && payrollTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				const updatePayloadData =
					payrollDataGridSelectedRows &&
					payrollDataGridSelectedRows.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriodStartDate: payPeriodStarDate,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						customEarnings: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.earnings),
						customDeductions: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.deductions),
						customReimbursements: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.reimbursements),
						// vacationPayAccured: parseFloat(item.vacationPayAccured)
						vacationPayAccured: item.vacationPayAccuredCalculated ? parseFloat(item.vacationPayAccuredCalculated) : null,
					}));
				(async () => {
					try {
						setPayrollSkeletonLoading(true)
						const response = await PayrollService.create({ payrollData: updatePayloadData });
						if (response && response.status === 201) {
							const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
							let calculatePayrollDataPayload = {
								data: {
									clientId: clientId,
									payPeriodStartDate: payPeriodStarDate,
									payPeriodEndDate: payPeriodEndDate,
									payrollSheduleId: selectedPayPeriodId,
									employeeIds: empIds
								},
								navigateToLogout: navigateToLogout,
							};
							calculatePayroll(calculatePayrollDataPayload);
						} else {
							setSnackbarData({
								open: true,
								message:
									response && response.message && response.message
										? response.message
										: "Something went wrong.",
								severity: "error",
							});
						}

					} catch { } finally { setPayrollSkeletonLoading(false) }
				})();
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const updatePayloadData =
				payrollSetupTableData &&
				payrollSetupTableData.map((item: any) => ({
					...item,
					clientId: clientId,
					payPeriodStartDate: payPeriodStarDate,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					customEarnings: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.earnings),
					customDeductions: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.deductions),
					customReimbursements: buildCustomEarningsArray(item, combineClientDetailsAndFrequencyDDL.salary_profile_edr.reimbursements),
					vacationPayAccured: parseFloat(item.vacationPayAccured)
				}));
			(async () => {
				try {
					setPayrollSkeletonLoading(true)
					const response = await PayrollService.create({ payrollData: updatePayloadData });
					if (response && response.status === 201) {
						const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
						let calculatePayrollDataPayload = {
							data: {
								clientId: clientId,
								payPeriodStartDate: payPeriodStarDate,
								payPeriodEndDate: payPeriodEndDate,
								payrollSheduleId: selectedPayPeriodId,
								employeeIds: empIds
							},
							navigateToLogout: navigateToLogout,
						};
						calculatePayroll(calculatePayrollDataPayload);
					} else {
						setSnackbarData({
							open: true,
							message:
								response && response.message && response.message
									? response.message
									: "Something went wrong.",
							severity: "error",
						});
					}

				} catch { } finally { setPayrollSkeletonLoading(false) }
			})();
		}
	};
	const onInsertButtonClicked = (selectedItem: any) => {

		if (payrollTableData && payrollTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)

				let insertEmployeePayrollDataPayload = {
					data: {
						clientId: clientId,
						payPeriodStartDate: payPeriodStarDate,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						employeeIds: empIds,
						insertingType: selectedItem === "Insert Gross Hours" ? 100 :
							selectedItem === "Insert Gross" ? 200 :
								selectedItem === "Insert Hours" ? 300 :
									selectedItem === "Insert Previous" ? 400 :
										selectedItem === "Insert EDR" ? 500 : selectedItem
					},
					navigateToLogout: navigateToLogout,
				};
				insertEmployeePayrollData(insertEmployeePayrollDataPayload);
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const empIds = payrollSetupTableData.map((x: any) => x.employeeId)

			let insertEmployeePayrollDataPayload = {
				data: {
					clientId: clientId,
					payPeriodStartDate: payPeriodStarDate,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					employeeIds: empIds,
					insertingType: selectedItem === "Insert Gross Hours" ? 100 :
						selectedItem === "Insert Gross" ? 200 :
							selectedItem === "Insert Hours" ? 300 :
								selectedItem === "Insert Previous" ? 400 :
									selectedItem === "Insert EDR" ? 500 : selectedItem
				},
				navigateToLogout: navigateToLogout,
			};
			insertEmployeePayrollData(insertEmployeePayrollDataPayload);
		}
	};





	// for calculated payroll buttons
	const onEditCalculatedPayrollButtonClicked = async () => {

		const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
		if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
			const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)

			if (payrollTableData && payrollTableData.rowSelection) {
				if (calculatedPayrollDataGridSelectedRows.length > 0) {
					const updatePayloadData =
						calculatedPayrollDataGridSelectedRows &&
						calculatedPayrollDataGridSelectedRows.map((item: any) => ({
							...item,
							clientId: clientId,
							payPeriod: selectedPayrollPeriod.payPeriodCode,
							payPeriodStartDate: payPeriodStarDate,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
						}));

					editingCalculatdePayroll({
						clientId,
						data: { payrollData: updatePayloadData },
						navigateToLogout: navigateToLogout
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Please select at least one employee!",
						severity: "error"
					})
				}
			} else {
				const updatePayloadData =
					calculatedPayrollTableData &&
					calculatedPayrollTableData.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriod: selectedPayrollPeriod.payPeriodCode,
						payPeriodStartDate: payPeriodStarDate,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
					}));
				editingCalculatdePayroll({
					clientId,
					data: { payrollData: updatePayloadData },
					navigateToLogout: navigateToLogout
				});
			}
		}
	};
	const onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler = async (selectedItem: any) => {
		// alert(selectedItem)
		const empIds = calculatedPayrollDataGridSelectedRows.map((x: any) => x.employeeId)
		if (selectedItem === 'Print Stub Only') {
			if (calculatedPayrollDataGridSelectedRows && calculatedPayrollDataGridSelectedRows.length > 0) {
				(async () => {
					let payload = {
						clientId: clientId,
						empIds
					}
					try {
						// setIsLoading(true)
						setCalculatedPayrollSkeletonLoading(true)
						const response: any = await PayrollService.createStubPayrollPdfPrint(
							payload
						);
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
								severity: "error",
							});
						} else {
							const filename = `StubPrint.pdf`;
							savePdfAsFile(response, filename);
							clearAllCalculatedPayrollDataGridRowsSelections();
						}
					} catch { } finally { setCalculatedPayrollSkeletonLoading(false) }
				})();
			} else {
				setSnackbarData({
					open: true,
					message: "Please select ateast one employee",
					severity: "error",
				})

			}
		}
	};

	// const onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler = async (): Promise<void> => {
	// 	const maskedStatus: any = sessionStorage.getItem("maskEnabled");
	// 	if (maskedStatus === "true") {
	// 		setSnackbarData({
	// 			open: true,
	// 			message: "Please unmask your data.",
	// 			severity: "error",
	// 		});
	// 	} else {

	// 		setIsLoading(true);
	// 		try {
	// 			const response: any = await PayrollService.createStubPayrollPdfPrint(
	// 				selectedRowsValues,
	// 				clientId,
	// 			);
	// 			if (response.status === 400) {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: response.data.message
	// 						? response.data.message
	// 						: "Something went wrong.",
	// 					// message: "Please Unmask sensitive data for PDF printing.",
	// 					severity: "error",
	// 				});
	// 			} else {
	// 				const filename = `EmailStubPrint.pdf`;
	// 				savePdfAsFile(response, filename);
	// 			}
	// 			setIsLoading(false);
	// 		} catch (err) {
	// 			alert(err);
	// 		}
	// 		setIsLoading(false);

	// 	}
	// };


	const onEmailStubCalculatedPayrollButtonClicked = async (selectedItem: any) => {
		alert(selectedItem)
	};


	// const onSubmitCalculatedPayrollButtonClickedHandler = () => {
	// 	const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
	// 	if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
	// 		const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
	// 		if (payrollTableData && payrollTableData.rowSelection) {
	// 			if (payrollDataGridSelectedRows.length > 0) {
	// 				const updatePayloadData =
	// 					payrollDataGridSelectedRows &&
	// 					payrollDataGridSelectedRows.map((item: any) => ({
	// 						...item,
	// 						clientId: clientId,
	// 						payPeriod: selectedPayrollPeriod.payPeriodCode,
	// 						payPeriodEndDate: payPeriodEndDate,
	// 						payrollSheduleId: selectedPayPeriodId,
	// 					}));
	// 				savingCalculatdePayroll({
	// 					clientId,
	// 					data: { payrollData: updatePayloadData },
	// 					navigateToLogout: navigateToLogout
	// 				});
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: "Please select at least one employee!",
	// 					severity: "error"
	// 				})
	// 			}
	// 		} else {
	// 			const updatePayloadData =
	// 				calculatedPayrollTableData &&
	// 				calculatedPayrollTableData.map((item: any) => ({
	// 					...item,
	// 					clientId: clientId,
	// 					payPeriod: selectedPayrollPeriod.payPeriodCode,
	// 					payPeriodEndDate: payPeriodEndDate,
	// 					payrollSheduleId: selectedPayPeriodId,
	// 				}));
	// 			savingCalculatdePayroll({
	// 				clientId,
	// 				data: { payrollData: updatePayloadData },
	// 				navigateToLogout: navigateToLogout
	// 			});
	// 		}
	// 	}
	// };
	const onSubmitOrOverWriteCalculatedPayrollButtonClickedHandler = (event: any) => {
		if (event === "Overwrite") {
			const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
			if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
				const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
				if (payrollTableData && payrollTableData.rowSelection) {
					if (calculatedPayrollDataGridSelectedRows.length > 0) {
						const updatePayloadData =
							calculatedPayrollDataGridSelectedRows &&
							calculatedPayrollDataGridSelectedRows.map((item: any) => ({
								...item,
								clientId: clientId,
								payPeriod: selectedPayrollPeriod.payPeriodCode,
								payPeriodStartDate: payPeriodStarDate,
								payPeriodEndDate: payPeriodEndDate,
								payrollSheduleId: selectedPayPeriodId,
							}));

						overwriteCalculatdePayroll({
							clientId,
							data: { payrollData: updatePayloadData },
							navigateToLogout: navigateToLogout
						});
					} else {
						setSnackbarData({
							open: true,
							message: "Please select at least one employee!",
							severity: "error"
						})
					}
				} else {
					const updatePayloadData =
						calculatedPayrollTableData &&
						calculatedPayrollTableData.map((item: any) => ({
							...item,
							clientId: clientId,
							payPeriod: selectedPayrollPeriod.payPeriodCode,
							payPeriodStartDate: payPeriodStarDate,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
						}));

					overwriteCalculatdePayroll({
						clientId,
						data: { payrollData: updatePayloadData },
						navigateToLogout: navigateToLogout
					});
				}
			}
		} else {
			const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
			if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
				const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
				if (payrollTableData && payrollTableData.rowSelection) {
					if (calculatedPayrollDataGridSelectedRows.length > 0) {
						const updatePayloadData =
							calculatedPayrollDataGridSelectedRows &&
							calculatedPayrollDataGridSelectedRows.map((item: any) => ({
								...item,
								clientId: clientId,
								payPeriod: selectedPayrollPeriod.payPeriodCode,
								payPeriodStartDate: payPeriodStarDate,
								payPeriodEndDate: payPeriodEndDate,
								payrollSheduleId: selectedPayPeriodId,
								checkDate: formatDate(item.checkDate),
							}));

						savingCalculatdePayroll({
							clientId,
							data: { payrollData: updatePayloadData },
							navigateToLogout: navigateToLogout
						});
					} else {
						setSnackbarData({
							open: true,
							message: "Please select at least one employee!",
							severity: "error"
						})
					}
				} else {
					const updatePayloadData =
						calculatedPayrollTableData &&
						calculatedPayrollTableData.map((item: any) => ({
							...item,
							clientId: clientId,
							payPeriod: selectedPayrollPeriod.payPeriodCode,
							payPeriodStartDate: payPeriodStarDate,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
							checkDate: formatDate(item.checkDate),
						}));
					savingCalculatdePayroll({
						clientId,
						data: { payrollData: updatePayloadData },
						navigateToLogout: navigateToLogout
					});
				}
			}
		}
	};
	const onProcessPayrollClicked = async () => {
		if (openRollbackDialogue) {
			(async () => {
				let requestPayload = {
					clientId: clientId,
					// paymentMethod: combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.transactionMethod && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.transactionMethod === 100 ? 300 : 100,
					// province: combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.address.province.id,
					// taxYear: taxYear,
					payPeriodFrequencyId: selectedPayPeriodId,
					payPeriodStarDate: payPeriodStarDate,
					payPeriodEndDate: payPeriodEndDate
				}
				try {
					setProcessRollBackPayrollLoading(true)
					const response = await PayrollService.processRollBack(requestPayload);
					if (response && response.status === 201) {
						queryClient.invalidateQueries({
							queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
						})
						setIsPayrollProcessed(true);
						queryClient.invalidateQueries({
							queryKey: [CALCULATED_PAYROLL_LIST_QUERY_KEY, clientId,
								selectedPayPeriodId,
								payPeriodStarDate,
								payPeriodEndDate,
								viewCalculatedFilter,
								nameCalculatedFilter,
								showWithAlphabetCalculatedFilter,
								departmentsCalculatedFilter,
								rowsPerPageItemsCalculatedFilter,
								pageCalculatedFilter,
								queryParamsCalculatedPayrollData,],
						});
						setOpenRollbackDialogue(false);
						// window.location.reload();
					} else {
						setSnackbarData({
							open: true,
							message:
								response && response.message && response.message
									? response.message
									: "Something went wrong.",
							severity: "error",
						});
					}
				} catch { setProcessRollBackPayrollLoading(false) } finally { setProcessRollBackPayrollLoading(false); }
			})();
		} else {
			(async () => {
				let requestPayload = {
					clientId: clientId,
					paymentMethod: combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.transactionMethod && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.transactionMethod === 100 ? 300 : 100,
					province: combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.address.province.id,
					taxYear: taxYear,
					payPeriodFrequencyId: selectedPayPeriodId,
					payPeriodEndDate: payPeriodEndDate
				}
				try {
					// setIsLoading(true)
					setCalculatedPayrollSkeletonLoading(true)
					const response = await PayrollService.process({ payrollData: requestPayload });
					if (response && response.status === 201) {
						(async () => {
							let requestPayload = {
								clientId: clientId,
								payPeriodFrequencyId: selectedPayPeriodId,
								payPeriodStarDate: payPeriodStarDate,
								payPeriodEndDate: payPeriodEndDate
							}
							try {
								setCalculatedPayrollSkeletonLoading(true)
								const response = await PayrollService.directDeposit({ payrollData: requestPayload });
							} catch { } finally { setCalculatedPayrollSkeletonLoading(false); }
						})();

						queryClient.invalidateQueries({
							queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
						})
						setIsPayrollProcessed(true);
						// dispatch(setSelectedINVOICE(response.data));
						// const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
						// let calculatePayrollDataPayload = {
						// 	data: {
						// 		clientId: clientId,
						// 		payPeriodEndDate: payPeriodEndDate,
						// 		payrollSheduleId: selectedPayPeriodId,
						// 		employeeIds: empIds
						// 	},
						// 	navigateToLogout: navigateToLogout,
						// };
						// calculatePayroll(calculatePayrollDataPayload);
					} else {
						setSnackbarData({
							open: true,
							message:
								response && response.message && response.message
									? response.message
									: "Something went wrong.",
							severity: "error",
						});
					}

				} catch { } finally { setCalculatedPayrollSkeletonLoading(false) }
			})();
		}
	};
	const onCancelProcessPayrollClicked = async () => {
		(async () => {
			let requestPayload = {
				clientId: clientId,
				invoiceNo: storedInvoiceNoId,
				payPeriodStartDate: payPeriodStarDate,
				payPeriodEndDate: payPeriodEndDate,
				payPeriodFrequencyId: selectedPayPeriodId,
			}

			try {
				setCalculatedPayrollSkeletonLoading(true)
				const response = await PayrollService.cancelProcess({ payrollData: requestPayload });
				if (response && response.status === 200) {
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					})
					setTimeout(() => {
						setIsPayrollProcessed(false)
					}, 1000)
					// dispatch(clearSelectedINVOICE());
					// const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
					// let calculatePayrollDataPayload = {
					// 	data: {
					// 		clientId: clientId,
					// 		payPeriodEndDate: payPeriodEndDate,
					// 		payrollSheduleId: selectedPayPeriodId,
					// 		employeeIds: empIds
					// 	},
					// 	navigateToLogout: navigateToLogout,
					// };
					// calculatePayroll(calculatePayrollDataPayload);
				} else {
					setSnackbarData({
						open: true,
						message:
							response && response.message && response.message
								? response.message
								: "Something went wrong.",
						severity: "error",
					});
				}

			} catch { } finally {
				setTimeout(() => {
					setCalculatedPayrollSkeletonLoading(false)
				}, 1000)
			}
		})();
	};

	const onSetPaymentDateClicked = async () => {
		const paymentDate = formMethods.watch('paymentDate')
		let payload = {
			payPeriodEndDate: payPeriodEndDate,
			payPeriodFrequencyId: selectedPayPeriodId,
			paymentDate: formatDate(paymentDate)
		}
		try {
			setIsLoading(true)
			const response = await PayrollService.setPaymentDate(payload);
			if (response && response.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success"
				});
				setIsPayrollProcessed(true)
				queryClient.invalidateQueries({
					queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
				})
			}
		} catch { setIsLoading(false) } finally { setIsLoading(false) }
	}

	const onClickImportPayrollDataHandler = () => {
		// alert('Comming Soon..')
		setOpenImportPayrollDataPanel(true)

	}

	// reset data after leaves component
	useRemoveQueriesOnUnmount([
		PAYROLL_LIST_QUERY_KEY,
		CALCULATED_PAYROLL_LIST_QUERY_KEY,
		// PAY_FREQUENCY_DDL_LIST_QUERY_KEY,
		// PAYROLL_PERIODS_DDL_LIST_QUERY_KEY,
		// "vocational-earnings-list",
		// "deaprtments-ddl",
		// EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY,
		COMBINE_API_FOR_CLIENT_DETAILS_AND_FREQUENCY_DDL
	]);

	// // payroll filters

	// const onChangeNameFilter = (value: any) => {
	// 	setNameFilter(value.value)
	// }
	// const ShowWithAlphabetFilter = (value: any) => {
	// 	SetShowWithAlphabetFilter(value.value)
	// }
	// const onChangePaidByFilter = (value: any) => {
	// 	setPaidByFilter(value.value)
	// }
	// const onChangeDepartmentsFilter = (value: any) => {
	// 	setDepartmentsFilter(value.value)
	// }
	// const onChangeRowsPerPageFilter = (value: any) => {
	// 	setRowsPerPageItemsFilter(value.value)
	// }

	// // calculated filters
	// const onChangeViewFilter = (value: any) => {
	// 	setViewCalculatedFilter(value.value)
	// }
	// const onChangeNameCalculatedFilter = (value: any) => {
	// 	setNameCalculatedFilter(value.value)
	// }
	// const ShowWithAlphabetCalculatedFilter = (value: any) => {
	// 	setShowWithAlphabetCalculatedFilter(value.value)
	// }
	// const onChangeDepartmentsCalculatedFilter = (value: any) => {
	// 	setDepartmentsCalculatedFilter(value.value)
	// }
	// const onChangeRowsPerPageCalculatedFilter = (value: any) => {
	// 	setRowsPerPageItemsCalculatedFilter(value.value)
	// }

	// const onChangeNameFilter = useCallback((value: any) => setNameFilter(value.value), []);
	// const ShowWithAlphabetFilter = useCallback((value: any) => SetShowWithAlphabetFilter(value.value), []);
	// const onChangePaidByFilter = useCallback((value: any) => setPaidByFilter(value.value), []);
	// const onChangeDepartmentsFilter = useCallback((value: any) => setDepartmentsFilter(value.value), []);
	// const onChangeRowsPerPageFilter = useCallback((value: any) => setRowsPerPageItemsFilter(value.value), []);
	// const onChangePageFilter = useCallback((value: any) => setPageFilter(value.value), []);

	// const onChangeViewFilter = useCallback((value: any) => setViewCalculatedFilter(value.value), []);
	// const onChangeNameCalculatedFilter = useCallback((value: any) => setNameCalculatedFilter(value.value), []);
	// const ShowWithAlphabetCalculatedFilter = useCallback((value: any) => setShowWithAlphabetCalculatedFilter(value.value), []);
	// const onChangeDepartmentsCalculatedFilter = useCallback((value: any) => setDepartmentsCalculatedFilter(value.value), []);
	// const onChangeRowsPerPageCalculatedFilter = useCallback((value: any) => setRowsPerPageItemsCalculatedFilter(value.value), []);
	// const onChangePageCalculatedFilter = useCallback((value: any) => setPageCalculatedFilter(value.value), []);

	const onChangeNameFilter = useCallback((value: any) => {
		setNameFilter(value.value);
		dispatch(setPayrollFilters({ name: value.value }));
	}, [dispatch]);

	const ShowWithAlphabetFilter = useCallback((value: any) => {
		SetShowWithAlphabetFilter(value.value);
		dispatch(setPayrollFilters({ startsWith: value.value }));
	}, [dispatch]);

	const onChangePaidByFilter = useCallback((value: any) => {
		setPaidByFilter(value.value);
		dispatch(setPayrollFilters({ paidBy: value.value }));
	}, [dispatch]);

	const onChangeDepartmentsFilter = useCallback((value: any) => {
		setDepartmentsFilter(value.value);
		dispatch(setPayrollFilters({ department: value.value }));
	}, [dispatch]);

	const onChangeRowsPerPageFilter = useCallback((value: any) => {
		setRowsPerPageItemsFilter(value.value);
		dispatch(setPayrollFilters({ pageSize: value.value }));
	}, [dispatch]);

	const onChangePageFilter = useCallback((value: any) => {
		setPageFilter(value.value);
		dispatch(setPayrollFilters({ page: value.value }));
	}, [dispatch]);

	const onChangeViewFilter = useCallback((value: any) => {
		setViewCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ viewCalculated: value.value }));
	}, [dispatch]);

	const onChangeNameCalculatedFilter = useCallback((value: any) => {
		setNameCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ nameCalculated: value.value }));
	}, [dispatch]);

	const ShowWithAlphabetCalculatedFilter = useCallback((value: any) => {
		setShowWithAlphabetCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ startsWithCalculated: value.value }));
	}, [dispatch]);

	const onChangeDepartmentsCalculatedFilter = useCallback((value: any) => {
		setDepartmentsCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ departmentCalculated: value.value }));
	}, [dispatch]);

	const onChangeRowsPerPageCalculatedFilter = useCallback((value: any) => {
		setRowsPerPageItemsCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ pageSizeCalculated: value.value }));
	}, [dispatch]);

	const onChangePageCalculatedFilter = useCallback((value: any) => {
		setPageCalculatedFilter(value.value);
		dispatch(setPayrollFilters({ pageCalculated: value.value }));
	}, [dispatch]);



	const [isLoadingExportFile, setIsLoadingExportFile] = useState(false)
	const onCloseImportPayrollDataPanel = () => {
		setOpenImportPayrollDataPanel(false);
	};
	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};
	const onExportButtonClick = async (tag: string) => {
		// alert(`Clicked Export Button => "${tag}"`)
		try {
			setIsLoadingExportFile(true)
			const response = await PayrollService.exportpayrollExcel(
				tag,
				clientId,
				selectedPayPeriodId,
				payPeriodStarDate,
				payPeriodEndDate,

				nameFilter,
				showWithAlphabetFilter,
				departmentsFilter,
				paidByFilter,
				rowsPerPageItemsFilter,
				pageFilter,
				queryParamsPayrollData,
				navigateToLogout
			);

			if (response) {
				if (response.status === 400) {
					setSnackbarData({
						open: true,
						message: response.data.message
							? response.data.message
							: "Something went wrong.",

						severity: "error",
					});
				} else {
					const filename = `${tag}.xlsx`;
					savePdfAsFile(response, filename);
				}
			} else {
				console.error("Error: No data received from the server");
			}

		} catch { setIsLoadingExportFile(false) } finally { setIsLoadingExportFile(false) }
	}
	const onImportDataClickedHandler = (tag: string, file: any) => {

		if (file) {
			const formData = new FormData();
			formData.append("file", file);
			let payload = {
				tag,
				clientId,
				formData,
				payPeriodEndDate,
				navigateToLogout
			};
			importPayrollDataClickedHandler(payload);
		} else {
			alert('Please choose a file to import.')
		}
	};

	const { isPending: isSubmittingImportData, mutate: importPayrollDataClickedHandler } =
		useMutation({
			mutationFn: PayrollService.importPayrollData,
			onSuccess: (res: any | IServerError | IResponse) => {
				if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: res.data.message,
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong",
						severity: "error",
					});
				}
				// }
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});


	const loadingRoollbackGetListApis = isPendingPayrollRollBackTableDate || isPendingCalculatedPayrollRollBackTableData || processRollBackPayrollLoading
	const onCloseRollbackDialogue = () => {
		// setOpenRollbackDialogue(false);
	};

	const EMPTY_ARRAY: any[] = [];
	const payrollRows = useMemo(() => payrollTableData?.items ?? EMPTY_ARRAY, [payrollTableData]);
	const payrollColumns = useMemo(() => payrollTableData?.headerColumns ?? EMPTY_ARRAY, [payrollTableData]);

	const calculatedPayrollRows = useMemo(() => calculatedPayrollTableData?.items ?? EMPTY_ARRAY, [calculatedPayrollTableData]);
	const calculatedPayrollColumns = useMemo(() => calculatedPayrollTableData?.headerColumns ?? EMPTY_ARRAY, [calculatedPayrollTableData]);

	const payrollRollBackRows = useMemo(() => payrollRollBackTableData?.items ?? EMPTY_ARRAY, [payrollRollBackTableData]);
	const payrollRollBackColumns = useMemo(() => payrollRollBackTableData?.headerColumns ?? EMPTY_ARRAY, [payrollRollBackTableData]);

	const calculatedPayrollRollBackRows = useMemo(() => calculatedPayrollRollBackTableData?.items ?? EMPTY_ARRAY, [calculatedPayrollRollBackTableData]);
	const calculatedPayrollRollBackColumns = useMemo(() => calculatedPayrollRollBackTableData?.headerColumns ?? EMPTY_ARRAY, [calculatedPayrollRollBackTableData]);


	const sumColumnWidth = (cols: any[]) =>
		cols?.reduce((sum: number, c: any) => sum + (Number(c.maxWidth) || 0), 0) ?? 0;

	const gridsMaxTotalWidth = useMemo(() => {
		return Math.max(
			sumColumnWidth(payrollColumns) + (payrollTableData?.rowSelection ? 36 : 0),
			sumColumnWidth(calculatedPayrollColumns) + (calculatedPayrollTableData?.rowSelection ? 36 : 0),
			// sumColumnWidth(payrollRollBackColumns),
			// sumColumnWidth(calculatedPayrollRollBackColumns),
		);
	}, [
		payrollColumns,
		calculatedPayrollColumns,
		// payrollRollBackColumns, 
		// calculatedPayrollRollBackColumns
	]);


	return (
		<div
			ref={gridsScrollContainerRef}
			style={{ overflowX: "auto", overflowY: "visible", width: "100%" }}
			className="payroll-grids-scroll-container"
		>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1.7rem"
				}}>
				<StickyHorizontalScrollbar scrollContainerRef={gridsScrollContainerRef} />
				<BackdropCircularProgress
					open={
						isPending ||
						isPendingPayrollPeriodsDDL ||
						isPendingCalculatedPayrollTableData ||
						loadingData ||
						isPendingCombineClientDetailsAndFrequencyDDL

						// || isSubmitting
						// || isPendingPayFrequencyDDL 
						// || isPendingDepartmentDDL 
						// || isDeletingEmployeePayrollData 
						// || isEditingCalculatdePayroll 
						// || isSavingCalculatdePayroll 
						// isOverwriteCalculatdePayroll ||
						// isPendingVocationalEarningsList ||
						// isCalculatingPayroll ||
						//  isPendingGetEmployeeDDLByStatus || isPendingClientInformationById || isPendingGeneralSettingsInformationById
					}
					testId="payroll-list"
				/>
				<DeleteConfirmationDialog
					open={openEmployeePayrollDataDialog}
					deleteActionHandler={deleteEmployeePayrollDataField}
					cancelActionHandler={onCloseEmployeePayrollDataDeleteHandler}
					testId="departments-confirmation-dialog"
				/>
				{combineClientDetailsAndFrequencyDDL &&
					combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
					!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCra ?
					<EnhancedSnackbarForLinkMessage
						message={(
							<>
								Setup{" "}
								{/* <a
									href="https://v3.simplepay.ca/#/taxes/remittance/cra"
									style={{
										color: theme.palette.primary.light,
										textDecoration: "none",
										fontWeight: "bold",
									}}
								>
									CRA Remittance
								</a> */}
								<Link
									to={`/taxes/remittance/cra`}
									className="no-underline"
									style={{ color: theme.palette.primary.light }}
								>
									CRA Remittance
								</Link>
								{" "}schedule first.
							</>
						)}
						onCloseHandler={onSnackbarClosed}
						severity={"warning"}
						testId={"snackbar"}
						open={true}
						autoHide={false}
					/>
					:
					combineClientDetailsAndFrequencyDDL &&
						combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
						combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "ON" &&
						!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveEht ?
						<EnhancedSnackbarForLinkMessage
							message={(
								<>
									Setup{" "}
									<Link
										to={`/taxes/eht/ontario`}
										className="no-underline"
										style={{ color: theme.palette.primary.light }}
									>
										EHT Remittance
									</Link>
									{" "}schedule first.
								</>
							)}
							onCloseHandler={onSnackbarClosed}
							severity={"warning"}
							testId={"snackbar"}
							open={true}
							autoHide={false}
						/>
						:
						combineClientDetailsAndFrequencyDDL &&
							combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
							combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "ON" &&
							!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveEhtSetting ?
							<EnhancedSnackbarForLinkMessage
								message={(
									<>
										Setup{" "}
										<Link
											to={`/taxes/eht/ontario`}
											className="no-underline"
											style={{ color: theme.palette.primary.light }}
										>
											EHT Remittance Setting
										</Link>
										{" "}first.
									</>
								)}
								onCloseHandler={onSnackbarClosed}
								severity={"warning"}
								testId={"snackbar"}
								open={true}
								autoHide={false}
							/>
							:
							(combineClientDetailsAndFrequencyDDL &&
								combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
								combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "QC" &&
								!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq)
								||
								(combineClientDetailsAndFrequencyDDL &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode !== "QC" &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll.qcEmployees &&
									!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq) ?
								<EnhancedSnackbarForLinkMessage
									message={(
										<>
											Setup{" "}
											<Link
												to={`/taxes/remittance/rq`}
												className="no-underline"
												style={{ color: theme.palette.primary.light }}
											>
												RQ Remittance
											</Link>
											{" "}schedule first.
										</>
									)}
									onCloseHandler={onSnackbarClosed}
									severity={"warning"}
									testId={"snackbar"}
									open={true}
									autoHide={false}
								/> :
								(combineClientDetailsAndFrequencyDDL &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "QC" &&
									combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq &&
									!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
									!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ||
									(combineClientDetailsAndFrequencyDDL &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode !== "QC" &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll.qcEmployees &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq &&
										!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
										!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ?
									<EnhancedSnackbarForLinkMessage
										message={(
											<>
												Setup{" "}
												<Link
													to={`/taxes/remittance/rq`}
													className="no-underline"
													style={{ color: theme.palette.primary.light }}
												>
													HSF profile
												</Link>
												{" "}and{" "}
												<Link
													to={`/taxes/remittance/rq`}
													className="no-underline"
													style={{ color: theme.palette.primary.light }}
												>
													CSST profile
												</Link>
												{" "}first.
											</>
										)}
										onCloseHandler={onSnackbarClosed}
										severity={"warning"}
										testId={"snackbar"}
										open={true}
										autoHide={false}
									/> :
									(combineClientDetailsAndFrequencyDDL &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "QC" && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq &&
										combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
										!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ||
										(combineClientDetailsAndFrequencyDDL &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode !== "QC" && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll.qcEmployees &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
											!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ?
										<EnhancedSnackbarForLinkMessage
											message={(
												<>
													Setup{" "}
													<Link
														to={`/taxes/remittance/rq`}
														className="no-underline"
														style={{ color: theme.palette.primary.light }}
													>
														HSF profile
													</Link>
													{" "}first.
												</>
											)}
											onCloseHandler={onSnackbarClosed}
											severity={"warning"}
											testId={"snackbar"}
											open={true}
											autoHide={false}
										/> :
										(combineClientDetailsAndFrequencyDDL &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode === "QC" && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq && !combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
											combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ||
											(combineClientDetailsAndFrequencyDDL &&
												combineClientDetailsAndFrequencyDDL.client_details_for_payroll &&
												combineClientDetailsAndFrequencyDDL.client_details_for_payroll?.address?.province?.shortCode !== "QC" && combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveRq &&
												combineClientDetailsAndFrequencyDDL.client_details_for_payroll.qcEmployees &&
												!combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveCsst &&
												combineClientDetailsAndFrequencyDDL.client_details_for_payroll.haveHsf) ?
											<EnhancedSnackbarForLinkMessage
												message={(
													<>
														Setup{" "}
														<Link
															to={`/taxes/remittance/rq`}
															className="no-underline"
															style={{ color: theme.palette.primary.light }}
														>
															CSST profile
														</Link>
														{" "}first.
													</>
												)}
												onCloseHandler={onSnackbarClosed}
												severity={"warning"}
												testId={"snackbar"}
												open={true}
												autoHide={false}
											/> : combineClientDetailsAndFrequencyDDL &&
												combineClientDetailsAndFrequencyDDL.client_details_for_payroll ?
												<FormProvider {...formMethods}>
													<form onSubmit={formMethods.handleSubmit(onSubmit)}>

														{/* Payroll Non-Calculated Data Table */}
														<PayrollSetupHeaderPanel
															payFrequencyDDLItems={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl}
															onFrequencyChangeHandler={onFrequencyChangeHandler}
															payrollPeriodsDDL={payrollPeriodsDDL}
															onPayrollPeriodsDDLChangeHandler={onPayrollPeriodsDDLChangeHandler}
															departmentsDDLItems={departmentsDDLItems}
															payrollTableData={payrollTableData}
															selectedpayPeriodIndex={selectedpayPeriodIndex}
															payPeriodStarDate={payPeriodStarDate}
															payPeriodEndDate={payPeriodEndDate}
															summary={calculatedPayrollTableData && calculatedPayrollTableData.summary}
															onClickImportPayrollDataHandler={onClickImportPayrollDataHandler}
															onChangeNameFilter={onChangeNameFilter}
															onChangePaidByFilter={onChangePaidByFilter}
															ShowWithAlphabetFilter={ShowWithAlphabetFilter}
															onChangeDepartmentsFilter={onChangeDepartmentsFilter}
															onChangeRowsPerPageFilter={onChangeRowsPerPageFilter}
															onChangePageFilter={onChangePageFilter}
															general_settings={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.general_settings}
															payrollDataItems={payrollTableData && payrollTableData.items}
															calculatedPayrollDataItems={calculatedPayrollTableData && calculatedPayrollTableData.items}
															gridsMaxTotalWidth={gridsMaxTotalWidth}
															pageSizeStored={pageSizeStored}
														/>
														{payrollTableData && payrollTableData.items && payrollTableData.items.length === 0 &&
															payrollTableData && payrollTableData.total === 0 ? <></> : payrollTableData && payrollTableData.total !== 0 &&
														<PayrollDataGrid
															columns={
																// payrollTableData ? payrollTableData.headerColumns : []
																payrollColumns
															}
															rows={
																// payrollTableData && upadtedPayrollItems
																// 	? upadtedPayrollItems
																// 	: []
																// payrollTableData
																// 	? payrollTableData?.items
																// 	: []
																payrollRows
															}
															// originalPayrollDataItems={payrollTableData && payrollTableData?.items}
															originalPayrollDataItems={payrollRows}
															showIndexColumn={false}
															showChecboxColumn={payrollTableData && payrollTableData.rowSelection}
															// showSettingColumn={true}
															// onRowEditClicked={onRowEditClicked}
															// onRowDeleteClicked={onRowDeleteClicked}
															// onSnackbarMessageData={(value: any) => setSnackbarData(value)}
															onSnackbarMessageData={setSnackbarData}
															totalRecords={
																payrollTableData && payrollTableData.total
																	? payrollTableData.total
																	: 0
															}
															initialPageSize={INITIAL_PAGE_SIZE}
															rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
															// onRequestParamsChange={onRequestParamsChangePayrollData}
															selectionToolbar={selectionToolbar}
															// selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
															searchFieldPlaceholder={"Search..."}
															pageTitle={"Payroll"}
															// PayrollTableData={(data: any) =>
															// 	setPayrollTableData(data)
															// }
															PayrollTableData={setPayrollTableData}
															tableMargin={"0"}
															tableScrollHeight={true}
															vocationalEarningsList={updatedVocationalEarningsList}
															clearAllSelectedItemsForPayrollTable={clearAllSelectedItemsForPayrollTable}
															isLoading={
																isInsertingEmployeePayrollData ||
																isSubmitting ||
																isCalculatingPayroll ||
																payrollSkeletonLoading ||
																isDeletingEmployeePayrollData
															}
															selectedPayPeriod={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl.find((x: any) => x.value === selectedPayPeriodId)}
															pageScrollContainerRef={gridsScrollContainerRef}
															gridsMaxTotalWidth={gridsMaxTotalWidth}
															enableFrozenColumns={true}
														/>
														}

														{payrollTableData && payrollTableData.items && payrollTableData.items.length === 0 &&
															payrollTableData && payrollTableData.total === 0 ? <></> : payrollTableData && payrollTableData.total !== 0 && (
																<PayrollSetupToolbar
																	payrollScreenPermissions={payrollScreenPermissions}
																	onSubmitButtonClickedHandler={onSubmitButtonClickedHandler}
																	onCalculatePayrollClicked={onCalculatePayrollClicked}
																	onInsertButtonClicked={onInsertButtonClicked}
																	onDeleteEmployeePayrollDataClicked={onDeleteEmployeePayrollDataClicked}
																	payrollTableData={payrollTableData && payrollTableData}
																	haveCalculatedTable={
																		calculatedPayrollTableData && calculatedPayrollTableData.items && calculatedPayrollTableData.items.length === 0 && calculatedPayrollTableData && calculatedPayrollTableData.total === 0 ? false : true
																	}
																	clientBankDDL={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL}
																	gridsMaxTotalWidth={gridsMaxTotalWidth}
																/>
															)}

														{/* Payroll Calculated Data Table */}
														<CalculatedPayrollSetupHeaderPanel
															departmentsDDLItems={departmentsDDLItems}
															calculatedPayrollTableData={calculatedPayrollTableData}
															onChangeNameCalculatedFilter={onChangeNameCalculatedFilter}
															ShowWithAlphabetCalculatedFilter={ShowWithAlphabetCalculatedFilter}
															onChangeViewFilter={onChangeViewFilter}
															onChangeDepartmentsCalculatedFilter={onChangeDepartmentsCalculatedFilter}
															onChangeRowsPerPageCalculatedFilter={onChangeRowsPerPageCalculatedFilter}
															gridsMaxTotalWidth={gridsMaxTotalWidth}
															onChangePageCalculatedFilter={onChangePageCalculatedFilter}
															pageSizeCalculatedStored={pageSizeCalculatedStored}

														/>
														{calculatedPayrollTableData && calculatedPayrollTableData.items && calculatedPayrollTableData.items.length === 0 &&
															calculatedPayrollTableData && calculatedPayrollTableData.total === 0 ? calculatedPayrollTableData && calculatedPayrollTableData.total !== 0 &&
														<></> :
															<CalculatedPayrollDataGrid
																columns={
																	// calculatedPayrollTableData ? calculatedPayrollTableData.headerColumns : []
																	calculatedPayrollColumns
																}
																rows={
																	// calculatedPayrollTableData && upadtedCalculatedPayrollItems
																	// 	? upadtedCalculatedPayrollItems
																	// 	: []
																	// calculatedPayrollTableData
																	// 	? calculatedPayrollTableData.items
																	// 	: []
																	calculatedPayrollRows
																}
																showIndexColumn={false}
																showChecboxColumn={calculatedPayrollTableData && calculatedPayrollTableData.rowSelection}
																// onSnackbarMessageData={(value: any) => setSnackbarData(value)}
																onSnackbarMessageData={setSnackbarData}
																totalRecords={
																	calculatedPayrollTableData && calculatedPayrollTableData.total
																		? calculatedPayrollTableData.total
																		: 0
																}
																initialPageSize={INITIAL_PAGE_SIZE}
																rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
																// onRequestParamsChange={onRequestParamsChangeCalculatedPayrollData}
																selectionToolbar={CalculatedPayrollDataGridSelectionToolbar}
																// selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
																searchFieldPlaceholder={"Search..."}
																pageTitle={"Payroll"}
																// PayrollTableData={(data: any) =>
																// 	setPayrollTableData(data)
																// }
																PayrollTableData={setPayrollTableData}
																tableMargin={"0"}
																tableScrollHeight={true}
																selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
																clearAllSelectedItemsForCalculatedPayrollTable={clearAllSelectedItemsForCalculatedPayrollTable}
																isLoading={
																	calculatedPayrollSkeletonLoading ||
																	isEditingCalculatdePayroll ||
																	isOverwriteCalculatdePayroll ||
																	isSavingCalculatdePayroll
																}
																pageScrollContainerRef={gridsScrollContainerRef}
																gridsMaxTotalWidth={gridsMaxTotalWidth}
																enableFrozenColumns={true}
															/>}

														{calculatedPayrollTableData && calculatedPayrollTableData.items && calculatedPayrollTableData.items.length === 0 &&
															calculatedPayrollTableData && calculatedPayrollTableData.total === 0 ? (<></>) : calculatedPayrollTableData && calculatedPayrollTableData.total !== 0 && (
																<CalculatedPayrollSetupToolbar
																	// onSubmitCalculatedPayrollButtonClickedHandler={onSubmitCalculatedPayrollButtonClickedHandler}
																	onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler={onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler}
																	onEditCalculatedPayrollButtonClicked={onEditCalculatedPayrollButtonClicked}
																	onEmailStubCalculatedPayrollButtonClicked={onEmailStubCalculatedPayrollButtonClicked}
																	onSubmitOrOverWriteCalculatedPayrollButtonClickedHandler={onSubmitOrOverWriteCalculatedPayrollButtonClickedHandler}
																	onProcessPayrollClicked={onProcessPayrollClicked}
																	onCancelProcessPayrollClicked={onCancelProcessPayrollClicked}
																	payrollNonCalculatedDataItems={payrollTableData && payrollTableData.items}
																	selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
																	payPeriodEndDate={payPeriodEndDate}
																	payPeriodStartDate={payPeriodStarDate}
																	companyInfo={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll}
																	calculatedPayrollTableData={calculatedPayrollTableData}
																	onSetPaymentDateClicked={onSetPaymentDateClicked}
																	formMethods={formMethods}
																	clientBankDDL={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL}
																	gridsMaxTotalWidth={gridsMaxTotalWidth}
																/>
															)}
													</form>
												</FormProvider> : <></>}
			</Grid>

			<EnhancedDialog
				dialogTitle="Import Payroll Data"
				key="Import Payroll Data - box"
				testId="Import-payroll-data"
				onCloseHandler={onCloseImportPayrollDataPanel}
				open={openImportPayrollDataPanel}

				dialogContent={
					<Box sx={{ maxHeight: "70vh", overflowY: "auto", pl: 1 }}>

						<ImportPayrollDataPanel
							onImportDataClickedHandler={onImportDataClickedHandler}
							onExportButtonClick={onExportButtonClick}
							isSubmitting={isSubmittingImportData}
							isLoadingExportFile={isLoadingExportFile}
						// title="Import in Payroll Data Panel"
						// instructions={[
						// 	"Click excel icon to download file.",
						// 	"Enter payroll data.",
						// 	"Save the excel file as tab delimited text file.",
						// 	"Upload tab delimited text file to import data.",
						// ]}

						/>

						{/* <ImportPayrollDataPanel
							title="Import Deduction Data"
							instructions={[
								"Click excel icon to download file.",
								"Enter deductions data.",
								"Save the excel file as tab delimited text file.",
								"Upload tab delimited text file to import data.",
							]}
								
						/>

						<ImportPayrollDataPanel
							title="Import Payroll and Deduction Data"
							instructions={[
								"Click excel icon to download file.",
								"Enter deductions  data.",
								"Save the excel file as tab delimited text file.",
								"Upload tab delimited text file to import data.",
							]}
								
						/> */}

					</Box>
				}
				showdividers={false}
				dialogAction={null}
			/>

			{/* ROLLBACK Dialogue */}
			<EnhancedDialog
				dialogTitle="Rollback Employee(s)"
				key="rollback-employee"
				testId="rollback-employees"
				onCloseHandler={onCloseRollbackDialogue}
				showCloseButton={false}
				open={openRollbackDialogue}
				dialogContent={
					<Box sx={{ maxHeight: "70vh", overflowY: "auto", pl: 1 }}>
						{loadingRoollbackGetListApis && <CircularProgress sx={{ width: "20px!important", height: "20px!important", "& .MuiCircularProgress-svg": { width: "20px", height: "20px" } }} />}
						<FormProvider {...formMethods}>
							<form onSubmit={formMethods.handleSubmit(onSubmit)}>
								<PayrollSetupHeaderPanel
									payFrequencyDDLItems={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl}
									onFrequencyChangeHandler={onFrequencyChangeHandler}
									payrollPeriodsDDL={payrollPeriodsDDL}
									onPayrollPeriodsDDLChangeHandler={onPayrollPeriodsDDLChangeHandler}
									departmentsDDLItems={departmentsDDLItems}
									payrollTableData={payrollRollBackTableData}
									selectedpayPeriodIndex={selectedpayPeriodIndex}
									payPeriodStarDate={payPeriodStarDate}
									payPeriodEndDate={payPeriodEndDate}
									summary={calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.summary}
									onClickImportPayrollDataHandler={onClickImportPayrollDataHandler}
									onChangeNameFilter={onChangeNameFilter}
									onChangePaidByFilter={onChangePaidByFilter}
									ShowWithAlphabetFilter={ShowWithAlphabetFilter}
									onChangeDepartmentsFilter={onChangeDepartmentsFilter}
									onChangeRowsPerPageFilter={onChangeRowsPerPageFilter}
									general_settings={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.general_settings}
									payrollDataItems={payrollRollBackTableData && payrollRollBackTableData.items}
									calculatedPayrollDataItems={calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.items}
									openRollbackDialogue={openRollbackDialogue}
									onChangePageFilter={onChangePageFilter}
								/>
								{payrollRollBackTableData && payrollRollBackTableData.items && payrollRollBackTableData.items.length === 0 &&
									payrollRollBackTableData && payrollRollBackTableData.total === 0 ? <></> : payrollRollBackTableData && payrollRollBackTableData.total !== 0 &&
								<PayrollDataGrid
									columns={
										// payrollRollBackTableData ? payrollRollBackTableData.headerColumns : []
										payrollRollBackColumns
									}
									rows={
										// payrollRollBackTableData && upadtedPayrollRollBackItems
										// 	? upadtedPayrollRollBackItems
										// 	: []
										// payrollRollBackTableData
										// 	? payrollRollBackTableData?.items
										// 	: []
										payrollRollBackRows
									}
									// originalPayrollDataItems={payrollRollBackTableData && payrollRollBackTableData?.items}
									originalPayrollDataItems={payrollRows}
									showIndexColumn={false}
									showChecboxColumn={payrollRollBackTableData && payrollRollBackTableData.rowSelection}
									// onSnackbarMessageData={(value: any) => setSnackbarData(value)}
									onSnackbarMessageData={setSnackbarData}
									totalRecords={
										payrollRollBackTableData && payrollRollBackTableData.total
											? payrollRollBackTableData.total
											: 0
									}
									initialPageSize={INITIAL_PAGE_SIZE}
									rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
									selectionToolbar={selectionToolbar}
									// selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
									searchFieldPlaceholder={"Search..."}
									pageTitle={"Payroll"}
									// PayrollTableData={(data: any) =>
									// 	setPayrollTableData(data)
									// }
									PayrollTableData={setPayrollTableData}
									tableMargin={"0"}
									tableScrollHeight={true}
									vocationalEarningsList={updatedVocationalEarningsList}
									clearAllSelectedItemsForPayrollTable={clearAllSelectedItemsForPayrollTable}
									isLoading={
										isInsertingEmployeePayrollData ||
										isSubmitting ||
										isCalculatingPayroll ||
										payrollSkeletonLoading ||
										isDeletingEmployeePayrollData
									}
									selectedPayPeriod={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.payroll_schedule_ddl.find((x: any) => x.value === selectedPayPeriodId)}
									pageScrollContainerRef={gridsScrollContainerRef}
									gridsMaxTotalWidth={gridsMaxTotalWidth}
									enableFrozenColumns={true}
								/>}

								{payrollRollBackTableData && payrollRollBackTableData.items && payrollRollBackTableData.items.length === 0 &&
									payrollRollBackTableData && payrollRollBackTableData.total === 0 ? <></> : payrollRollBackTableData && payrollRollBackTableData.total !== 0 && (
										<PayrollSetupToolbar
											payrollScreenPermissions={payrollScreenPermissions}
											onSubmitButtonClickedHandler={onSubmitButtonClickedHandler}
											onCalculatePayrollClicked={onCalculatePayrollClicked}
											onInsertButtonClicked={onInsertButtonClicked}
											onDeleteEmployeePayrollDataClicked={onDeleteEmployeePayrollDataClicked}
											payrollTableData={payrollRollBackTableData && payrollRollBackTableData}
											haveCalculatedTable={
												calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.items && calculatedPayrollRollBackTableData.items.length === 0 && calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total === 0 ? false : true
											}
											clientBankDDL={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL}
											openRollbackDialogue={openRollbackDialogue}
										/>
									)}
								<CalculatedPayrollSetupHeaderPanel
									departmentsDDLItems={departmentsDDLItems}
									calculatedPayrollTableData={calculatedPayrollRollBackTableData}
									onChangeNameCalculatedFilter={onChangeNameCalculatedFilter}
									ShowWithAlphabetCalculatedFilter={ShowWithAlphabetCalculatedFilter}
									onChangeViewFilter={onChangeViewFilter}
									onChangeDepartmentsCalculatedFilter={onChangeDepartmentsCalculatedFilter}
									onChangeRowsPerPageCalculatedFilter={onChangeRowsPerPageCalculatedFilter}
									onChangePageCalculatedFilter={onChangePageCalculatedFilter}
								/>
								{calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.items && calculatedPayrollRollBackTableData.items.length === 0 &&
									calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total === 0 ? calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total !== 0 &&
								<></> :
									<CalculatedPayrollDataGrid
										columns={
											// calculatedPayrollRollBackTableData ? calculatedPayrollRollBackTableData.headerColumns : []
											calculatedPayrollRollBackColumns
										}
										rows={
											// calculatedPayrollRollBackTableData && upadtedCalculatedPayrollRollBackItems
											// 	? upadtedCalculatedPayrollRollBackItems
											// 	: []
											// calculatedPayrollRollBackTableData
											// 	? calculatedPayrollRollBackTableData.items
											// 	: []
											calculatedPayrollRollBackRows
										}
										showIndexColumn={false}
										showChecboxColumn={calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.rowSelection}
										// onSnackbarMessageData={(value: any) => setSnackbarData(value)}
										onSnackbarMessageData={setSnackbarData}
										totalRecords={
											calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total
												? calculatedPayrollRollBackTableData.total
												: 0
										}
										initialPageSize={INITIAL_PAGE_SIZE}
										rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
										// onRequestParamsChange={onRequestParamsChangeCalculatedPayrollData}
										selectionToolbar={CalculatedPayrollDataGridSelectionToolbar}
										// selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
										searchFieldPlaceholder={"Search..."}
										pageTitle={"Payroll"}
										// PayrollTableData={(data: any) =>
										// 	setPayrollTableData(data)
										// }
										PayrollTableData={setPayrollTableData}
										tableMargin={"0"}
										tableScrollHeight={true}
										selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
										clearAllSelectedItemsForCalculatedPayrollTable={clearAllSelectedItemsForCalculatedPayrollTable}
										isLoading={
											calculatedPayrollSkeletonLoading ||
											isEditingCalculatdePayroll ||
											isOverwriteCalculatdePayroll ||
											isSavingCalculatdePayroll
										}
										pageScrollContainerRef={gridsScrollContainerRef}
										gridsMaxTotalWidth={gridsMaxTotalWidth}
										enableFrozenColumns={true}
									/>}

								{calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.items && calculatedPayrollRollBackTableData.items.length === 0 &&
									calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total === 0 ? (<></>) : calculatedPayrollRollBackTableData && calculatedPayrollRollBackTableData.total !== 0 && (
										<CalculatedPayrollSetupToolbar
											onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler={onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler}
											onEditCalculatedPayrollButtonClicked={onEditCalculatedPayrollButtonClicked}
											onEmailStubCalculatedPayrollButtonClicked={onEmailStubCalculatedPayrollButtonClicked}
											onSubmitOrOverWriteCalculatedPayrollButtonClickedHandler={onSubmitOrOverWriteCalculatedPayrollButtonClickedHandler}
											onProcessPayrollClicked={onProcessPayrollClicked}
											onCancelProcessPayrollClicked={onCancelProcessPayrollClicked}
											payrollNonCalculatedDataItems={payrollTableData && payrollTableData.items}
											selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
											payPeriodEndDate={payPeriodEndDate}
											payPeriodStartDate={payPeriodStarDate}
											companyInfo={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL.client_details_for_payroll}
											calculatedPayrollTableData={calculatedPayrollRollBackTableData}
											onSetPaymentDateClicked={onSetPaymentDateClicked}
											formMethods={formMethods}
											openRollbackDialogue={openRollbackDialogue}
											clientBankDDL={combineClientDetailsAndFrequencyDDL && combineClientDetailsAndFrequencyDDL}
										/>
									)}
							</form>
						</FormProvider>
					</Box>
				}
				showdividers={false}
				dialogAction={null}
				dialogMaxWidth={1550}
				showTopAndSideMenu={true}
				topMenuSpace={112}
				leftMenuSpace={180}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</div>
	);
};
